import type { BlogPost } from "./blog";

export const BLOG_CUISINE_PART3: BlogPost[] = [
  {
    "slug": "russkaya-kukhnya-restoran-traditsii",
    "title": "Русская кухня в ресторане: традиции и современная интерпретация",
    "metaTitle": "Русская кухня в ресторане — традиции и новые блюда | Паруса на крыше",
    "metaDescription": "Русская кухня в современном ресторане: что заказать, какие блюда считаются классикой и как шефы переосмысляют традиции. Обзор для гостей Петербурга.",
    "keywords": "русская кухня ресторан петербург, традиционная русская кухня, блюда русской кухни в ресторане",
    "date": "2026-06-05",
    "category": "Кухня и еда",
    "readTime": 7,
    "image": "/photos/food.webp",
    "lead": "Русская кухня переживает ренессанс. После десятилетий, когда «высокая кухня» ассоциировалась с французскими или итальянскими блюдами, российские рестораны всё активнее обращаются к родным традициям. Петербург — идеальное место для этого открытия: город с историей, в котором русская кулинарная традиция переплеталась с европейской.",
    "sections": [
      {
        "heading": "Что такое настоящая русская кухня",
        "body": "Русская кухня — это не только борщ и пельмени. Это целый пласт культуры: рецепты из монастырей, купеческие застолья, поварские книги XIX века. Её основа — сезонные продукты, умение работать с зерном, грибами, рыбой, ягодами. Квашение, соление, томление в печи — техники, которые сегодня снова в моде под названием ферментации и low&slow cooking.",
        "list": [
          "Грибные супы, солянки, уха — основа горячих блюд",
          "Пироги, кулебяки, расстегаи — уникальная выпечка",
          "Телятина, дичь, речная рыба — традиционные белки",
          "Квашеные и солёные заготовки — русская ферментация"
        ]
      },
      {
        "heading": "Современная интерпретация в ресторане",
        "body": "Лучшие петербургские шефы не копируют старые рецепты — они берут их за основу и создают новое. Щи из фермерской капусты с краковской колбасой ручного копчения. Уха с трюфельным маслом. Пирог с сёмгой и шпинатом вместо традиционного с визигой. Это не «испорченная» классика — это живой диалог традиции с современностью. В «Парусах на крыше» авторская кухня построена именно на таком подходе: уважение к продукту и технике, смелость в сочетаниях.",
        "highlight": "Авторская кухня «Парусов» — это петербургский характер: сдержанность в подаче, глубина во вкусе. Средний чек 1 300 ₽/чел, бизнес-ланч от 590 ₽."
      },
      {
        "heading": "Что заказать из русских блюд",
        "body": "В хорошем ресторане всегда есть блюда, которые рассказывают историю места. В Петербурге это обязательно рыба: форель, сёмга, судак. Грибы — белые, лисички, маслята в сезон. Дичь осенью. Ягодные соусы к мясу. Если хотите познакомиться с русской кухней по-настоящему — спросите официанта о блюдах из сезонного меню: именно там шеф показывает лучшее, что даёт местный рынок в этот момент.",
        "list": [
          "Уха или рыбный суп — обязательно попробуйте",
          "Блюда из белых грибов в сезон (август–октябрь)",
          "Сёмга или форель в любой подаче",
          "Пироги и выпечка — лучший финал русского обеда"
        ]
      },
      {
        "heading": "Петербург и русская кухня",
        "body": "Петербург — особый гастрономический регион. Близость Финского залива даёт свежую рыбу и морепродукты. Карелия — грибы и ягоды. Ленинградская область — фермерские продукты. Всё это питает петербургскую кухню. Рестораны на Марата, Рубинштейна, вокруг Невского — настоящая лаборатория, где русская традиция встречается с мировыми трендами. «Паруса на крыше» на 6-м этаже Olympic Plaza — отличная точка для этого знакомства с видом на центр города."
      }
    ],
    "cta": { "text": "Забронировать стол", "href": "/" }
  },
  {
    "slug": "kak-chitat-menyu-restorana",
    "title": "Как читать меню ресторана: что значат термины и как выбрать блюдо",
    "metaTitle": "Как читать меню ресторана — гид по терминам | Паруса на крыше",
    "metaDescription": "Непонятные слова в меню ресторана? Объясняем: конфи, тартар, карпаччо, редукция, эмульсия и другие термины. Как выбрать блюдо правильно.",
    "keywords": "как читать меню ресторана, термины меню ресторан, что значит конфи тартар карпаччо",
    "date": "2026-06-05",
    "category": "Кухня и еда",
    "readTime": 6,
    "image": "/photos/food.webp",
    "lead": "Открываете меню ресторана — и видите «утиное конфи с редукцией из черники на муслиновом пюре». Понятно только одно: здесь утка. Что остальное означает и как это повлияет на вкус? Разбираем ключевые термины, которые встречаются в меню современных ресторанов.",
    "sections": [
      {
        "heading": "Техники приготовления",
        "body": "Конфи — медленное томление в жире при низкой температуре (60–80°C). Мясо или рыба получаются очень нежными. Гриль — приготовление на открытом огне или решётке с характерными полосами и дымным ароматом. Су-вид — вакуумный пакет + водяная баня, точный контроль температуры. Результат: идеальная текстура без потери сока. Карпаччо — тонко нарезанное сырое мясо или рыба, обычно приправленное маслом и лимоном.",
        "list": [
          "Конфи — томление в жире, очень нежно",
          "Су-вид — вакуум + точная температура, сочно",
          "Гриль — огонь и решётка, характерный вкус дыма",
          "Карпаччо — сырое, тонко нарезанное, свежее"
        ]
      },
      {
        "heading": "Соусы и заправки",
        "body": "Редукция — уваренный соус (бульон, вино, сок), концентрированный по вкусу. Эмульсия — смесь масла и воды, взбитая до кремовой консистенции (например, голландский соус или айоли). Велюте — белый соус на основе бульона. Демигляс — насыщенный тёмный соус из говяжьих костей, варится часами. Бёр-блан — белое масляное вино с лимоном, классика к рыбе.",
        "highlight": "Когда официант говорит «соус по сезону» — это хороший знак: шеф работает со свежими продуктами, а не с заготовками."
      },
      {
        "heading": "Текстуры и подача",
        "body": "Муслиновое пюре — очень нежное пюре с большим количеством масла и сливок, почти воздушное. Тapenade — паста из оливок, анчоусов и каперсов (средиземноморская). Чипсы в меню ресторана — это не картофельные чипсы из пачки, а тонкие хрустящие лепёшки из картофеля, пармезана или овощей, запечённые в духовке. Хрустящие — элемент, добавляемый для контраста текстур. Кремё — насыщенный крем, более плотный, чем мусс.",
        "list": [
          "Муслиновое — очень нежное, воздушное",
          "Кремё — плотный насыщенный крем",
          "Хрустящее — для контраста текстур",
          "Мусс — лёгкое, воздушное, взбитое"
        ]
      },
      {
        "heading": "Как выбрать блюдо",
        "body": "Несколько правил для уверенного выбора. Спросите официанта о рекомендациях шефа — сезонные блюда обычно лучше всего. Если видите «фермерский» или «локальный» продукт — это хороший знак. Тартар и карпаччо — сырые блюда, важно понимать это заранее. В «Парусах на крыше» меню построено так, что каждое блюдо имеет описание, а команда зала готова объяснить состав и способ приготовления любой позиции."
      }
    ],
    "cta": { "text": "Посмотреть меню", "href": "/menu" }
  },
  {
    "slug": "morskie-grebeshki-kak-gotovyat-restoran",
    "title": "Морские гребешки в ресторане: как их готовят и как выбрать правильно",
    "metaTitle": "Морские гребешки в ресторане — как выбрать и что заказать | Паруса",
    "metaDescription": "Всё о морских гребешках в ресторане: как их готовят, обжаривают, подают. Как выбрать свежие, с чем сочетаются и что ожидать от блюда.",
    "keywords": "морские гребешки ресторан, жареные гребешки ресторан, гребешки блюдо что заказать",
    "date": "2026-06-06",
    "category": "Кухня и еда",
    "readTime": 5,
    "image": "/photos/food.webp",
    "lead": "Морской гребешок — один из самых интересных морепродуктов в ресторанном меню. При правильном приготовлении он сочетает нежную текстуру и лёгкий сладковатый вкус. При неправильном — резиновый и безвкусный. Разбираем, что нужно знать перед заказом.",
    "sections": [
      {
        "heading": "Что такое морской гребешок",
        "body": "Морской гребешок (Pecten maximus и родственные виды) — двустворчатый моллюск. В ресторанах чаще всего подают мускул — плотную белую часть, которая управляет створками. Реже — с икрой (кораллового цвета). Наши гребешки в основном приходят с Сахалина, Приморского края, а также импортируются из Исландии, Нормандии, Японии. Нормандские и японские считаются эталонными по вкусу.",
        "list": [
          "Мускул — плотная белая часть, самое вкусное",
          "Коралл (икра) — оранжевая часть, насыщенный вкус",
          "Российские — Сахалин, Приморье",
          "Импортные — Нормандия, Исландия, Япония"
        ]
      },
      {
        "heading": "Как правильно готовят гребешки",
        "body": "Идеальный гребешок — обжаренный на сильном огне в течение 90 секунд с каждой стороны. Результат: золотистая корочка снаружи и чуть сырой, нежный центр. Это называется «сир». Если гребешок пересушен — вся нежность теряется. Именно поэтому гребешки — лакмусовая бумага для оценки кухни: шеф, умеющий правильно обжарить гребешок, умеет работать с морепродуктами в целом.",
        "highlight": "Если гребешок белый и мягкий на срезе — он правильно приготовлен. Если серый и плотный по всей толщине — пересушен."
      },
      {
        "heading": "С чем подают гребешки",
        "body": "Классические сочетания: гребешки + цветная капуста (пюре, запечённая), горошек, спаржа, трюфель. Соусы: бёр-блан (масляно-винный), лимонное масло, веганская редукция из апельсина. Гребешки хорошо сочетаются с шампанским, белым бургундским или северным альзасским вином. В «Парусах на крыше» гребешки входят в состав авторских блюд с учётом сезонности: подача и гарнир меняются в зависимости от того, что есть у поставщиков.",
        "list": [
          "Пюре из цветной капусты — классика",
          "Спаржа и горошек — весенние сочетания",
          "Трюфельный соус — роскошный вариант",
          "Шампанское или белое бургундское — идеальный аперитив"
        ]
      }
    ],
    "cta": { "text": "Забронировать стол", "href": "/" }
  },
  {
    "slug": "chto-takoe-tartar-blyudo",
    "title": "Что такое тартар: история блюда, виды и как его едят в ресторане",
    "metaTitle": "Тартар — что это за блюдо, история и виды | Паруса на крыше",
    "metaDescription": "Тартар в меню ресторана: что это, из чего делают, бывает ли тартар из рыбы и овощей, как его едят и с чем сочетается.",
    "keywords": "что такое тартар блюдо, тартар из говядины ресторан, тартар из лосося меню",
    "date": "2026-06-06",
    "category": "Кухня и еда",
    "readTime": 5,
    "image": "/photos/food.webp",
    "lead": "Тартар — одно из тех блюд, которые пугают незнакомца и восхищают гурмана. Сырое рубленое мясо или рыба, приправленные каперсами, луком, желтком. Звучит странно — на вкус превосходно. Откуда взялось это блюдо и почему оно до сих пор в топе ресторанных меню?",
    "sections": [
      {
        "heading": "История тартара",
        "body": "Легенда гласит, что татарские воины клали мясо под сёдла коней — оно «размягчалось» и становилось съедобным без огня. Это миф, конечно. Реальная история: в начале XX века парижские ресторанные повара начали подавать сырую говядину с яичным желтком и каперсами под названием «Бифштекс à la Russe». Со временем блюдо получило название «Бефстроганов» в одних меню и «тартар» — в других. Сегодня тартар — это классика высокой кухни.",
        "list": [
          "Впервые в меню — Париж, начало XX века",
          "Классическая основа — говядина, желток, каперсы",
          "Сейчас — сёмга, тунец, овощи и даже грибы",
          "Блюдо холодное, готовится непосредственно перед подачей"
        ]
      },
      {
        "heading": "Виды тартара",
        "body": "Классический тартар из говядины — вырезка, рубленая ножом (не в мясорубке), с желтком, дижонской горчицей, каперсами, луком-шалот. Тартар из сёмги или тунца — популярнее в ресторанах, потому что рыба воспринимается как «менее рискованная» для тех, кто боится сырого мяса. Овощной тартар — свёкла, авокадо, томаты — модный вариант для вегетарианцев. В хорошем ресторане тартар всегда готовится из охлаждённого (не замороженного) продукта высшего качества.",
        "highlight": "Тартар никогда не подогревают — это холодное блюдо. Если его «приготовили» — это уже не тартар."
      },
      {
        "heading": "Как есть тартар правильно",
        "body": "Тартар подают с тостами или хрустящими хлебцами. Иногда к нему идёт гарнир — листья салата, хрустящий картофель. Едят тартар, набирая его на тост или ложкой. В ресторанах с хорошей репутацией можно не бояться: продукт тщательно отбирается. Если вы не уверены — спросите официанта, откуда мясо и как давно оно поступило. Хороший ресторан ответит без заминки.",
        "list": [
          "Подают с тостами — кладите на хлеб",
          "Температура: холодное, не комнатное",
          "Спросите об источнике — хороший ресторан расскажет",
          "Запивайте красным вином или светлым пивом"
        ]
      }
    ],
    "cta": { "text": "Посмотреть меню", "href": "/menu" }
  },
  {
    "slug": "risotto-klassika-italyanskoy-kukhni",
    "title": "Ризотто: классика итальянской кухни — как его готовят и почему оно вкусное",
    "metaTitle": "Ризотто — классика итальянской кухни | Паруса на крыше",
    "metaDescription": "Что такое ризотто, как его правильно готовят, какие виды бывают и почему хорошее ризотто — признак мастерства шефа. Обзор для ценителей.",
    "keywords": "ризотто итальянская кухня, ризотто ресторан петербург, правильное ризотто как готовят",
    "date": "2026-06-06",
    "category": "Кухня и еда",
    "readTime": 6,
    "image": "/photos/food.webp",
    "lead": "Ризотто — блюдо, которое легко испортить и сложно сделать идеальным. Именно поэтому в хорошем ресторане правильное ризотто — знак мастерства. В Петербурге итальянская кухня популярна, и ризотто встречается в меню многих заведений. Но вкусное ризотто — большая редкость.",
    "sections": [
      {
        "heading": "Что делает ризотто ризотто",
        "body": "Ризотто — это не просто рис. Это метод приготовления: рис (сорта арборио, карнароли или виалоне нано) обжаривается в масле, затем постепенно, половником за половником, вливается горячий бульон. Крахмал, который выделяется из риса при постоянном помешивании, создаёт кремовую текстуру без использования сливок. Финальный штрих — «мантекатура»: вмешивание холодного сливочного масла и тёртого пармезана. Это делает ризотто шелковистым.",
        "list": [
          "Сорт риса: арборио, карнароли, виалоне нано",
          "Постепенное добавление бульона — не всё сразу",
          "Постоянное помешивание — выпускает крахмал",
          "Мантекатура — масло и пармезан в конце"
        ]
      },
      {
        "heading": "Виды ризотто",
        "body": "Ризотто бьянко — базовое, без добавок, только рис, бульон, вино, масло и пармезан. Ризотто с грибами (фунги) — самое популярное, особенно с белыми или трюфелем. Ризотто с морепродуктами (маре) — без пармезана (рыба и сыр не сочетаются в итальянской традиции). Ризотто ала миланезе — с шафраном, золотистое, классика Милана. В «Парусах» ризотто готовится с сезонными добавками: летом — спаржа, осенью — белые грибы.",
        "highlight": "Правильное ризотто течёт на тарелке, как «волна» — all'onda по-итальянски. Слишком плотное — переварено, слишком жидкое — недоварено."
      },
      {
        "heading": "Почему ризотто — тест для шефа",
        "body": "Ризотто нельзя приготовить заранее — оно готовится к подаче. Минимум 18 минут активной работы. Если в ресторане ризотто приносят через 5 минут — значит, оно было заготовлено заранее. Шеф, который умеет делать настоящее ризотто, умеет работать в ритме кухни и слышать гостя. В «Парусах на крыше» кухня работает именно в таком режиме: авторские блюда готовятся к подаче, а не разогреваются.",
        "list": [
          "Время приготовления: 18–20 минут — нормально",
          "Если за 5 минут — значит заготовлено, не свежее",
          "Должно быть al dente — с лёгкой упругостью в центре",
          "Не должно прилипать к тарелке комком"
        ]
      }
    ],
    "cta": { "text": "Забронировать стол", "href": "/" }
  },
  {
    "slug": "tiramisu-istoriya-deserta",
    "title": "Тирамису: история, классический рецепт и как выбрать хорошее в ресторане",
    "metaTitle": "Тирамису — история десерта и как выбрать в ресторане | Паруса на крыше",
    "metaDescription": "История тирамису, классический рецепт, чем отличается хорошее от плохого и почему этот итальянский десерт до сих пор остаётся фаворитом ресторанов.",
    "keywords": "тирамису история десерт, тирамису в ресторане, классический тирамису рецепт",
    "date": "2026-06-07",
    "category": "Кухня и еда",
    "readTime": 5,
    "image": "/photos/food.webp",
    "lead": "Тирамису — самый известный итальянский десерт в мире. За этим именем скрываются савоярди, пропитанные эспрессо, и крем из маскарпоне с яичными желтками. Но почему одни тирамису восхитительны, а другие оставляют только разочарование?",
    "sections": [
      {
        "heading": "История десерта",
        "body": "Тирамису появился в Венето, Италия, примерно в 1960-х годах. Его создание приписывают кулинарам разных ресторанов — Ле Бекерие в Тревизо, Эль Тулле в Тревизо. Имя в переводе означает «поднять меня» или «подними мне настроение» (tira — тяни, mi — меня, su — вверх). Кофеин из эспрессо, сахар, яйца — всё это придаёт лёгкую эйфорию. Отсюда и название.",
        "list": [
          "Родина — Венето, Северная Италия, 1960-е",
          "Перевод: «поднять мне настроение»",
          "Официально включён в список классических итальянских блюд",
          "Ежегодно в мире съедают миллионы порций"
        ]
      },
      {
        "heading": "Классический состав",
        "body": "Настоящий тирамису: savоiardi (сухое итальянское печенье), пропитанное крепким эспрессо с добавлением амаретто или марсалы. Крем: маскарпоне + яичные желтки + сахар. Всё взбивается и выкладывается слоями. Сверху — какао-порошок. Никаких сливок, желатина, ванили. Варианты с этими добавками — это другой десерт с именем тирамису.",
        "highlight": "Настоящий тирамису делается без желатина — держит форму за счёт маскарпоне и желтков. Если тирамису твёрдый как желе — перед вами «стабилизированная» версия."
      },
      {
        "heading": "Как выбрать хорошее тирамису",
        "body": "Признаки правильного тирамису в ресторане: крем нежный, но держит форму; печенье пропитано, но не раскисло полностью — должна ощущаться текстура; кофейный вкус явный; какао сверху свежее, не залежалось. Плохой тирамису: резиновый крем (желатин), безвкусное печенье (не пропитано или пропитано слишком давно), слабый кофейный аромат. В «Парусах на крыше» тирамису готовится ежедневно — не хранится неделями.",
        "list": [
          "Крем нежный, но не жидкий",
          "Печенье пропитано — но с текстурой",
          "Кофейный вкус выраженный",
          "Какао свежее, не слипшееся"
        ]
      }
    ],
    "cta": { "text": "Посмотреть меню с десертами", "href": "/menu" }
  },
  {
    "slug": "panna-kotta-italyanskiy-desert",
    "title": "Панна котта: итальянский десерт — виды, соусы и секреты правильной текстуры",
    "metaTitle": "Панна котта — виды и правильная текстура | Паруса на крыше",
    "metaDescription": "Всё о панна котте: история, классический рецепт, виды (ваниль, шоколад, ягодная), с чем подавать и как отличить хорошую от плохой.",
    "keywords": "панна котта десерт ресторан, панна котта что это, виды панна котты",
    "date": "2026-06-07",
    "category": "Кухня и еда",
    "readTime": 5,
    "image": "/photos/food.webp",
    "lead": "Панна котта — буквально «варёные сливки» по-итальянски. Один из самых элегантных десертов мирового ресторанного меню: минимум ингредиентов, максимум нежности. Но за простотой скрывается точность: одна лишняя капля желатина — и вместо шёлка получите резину.",
    "sections": [
      {
        "heading": "Что такое панна котта",
        "body": "Панна котта родом из Пьемонта, Северная Италия. Базовый рецепт: сливки + сахар + ваниль + желатин. Смесь нагревают, растворяют желатин, заливают в формы, охлаждают. Подают перевёрнутой на тарелку или прямо в стекле/форме. Правильная панна котта дрожит — именно это дрожание говорит о правильном количестве желатина. Она не должна быть жёсткой.",
        "list": [
          "Ингредиенты: сливки, сахар, ваниль, желатин",
          "Должна дрожать — признак правильной текстуры",
          "Подаётся холодной",
          "Родина — Пьемонт, Италия"
        ]
      },
      {
        "heading": "Виды и сочетания",
        "body": "Классическая — ваниль, подаётся с ягодным кули (малина, клубника, черника). Шоколадная — часть сливок заменяют шоколадом. Кофейная — с добавлением эспрессо. Матча — с японским зелёным чаем, модный вариант. Соляная карамель — в паре с классической ванильной. В «Парусах» панна котта меняется по сезону: летом — с садовой клубникой, зимой — с апельсиновым кули и специями.",
        "highlight": "Ягодный кули — это не варенье. Это свежая ягода, пробитая блендером и слегка подслащённая. Он должен быть кислым, а не сладким — для баланса."
      },
      {
        "heading": "Как отличить хорошую от плохой",
        "body": "Хорошая панна котта: гладкая поверхность, нежная текстура, дрожит при движении тарелки, вкус чистый и молочный. Плохая: жёсткая как желе, безвкусная, с пузырями на поверхности (значит, быстро охлаждали), привкус желатина. Правило: если панна котта режется ножом без усилий — всё в порядке. Если нужно давить — желатина много.",
        "list": [
          "Хорошая — дрожит и гладкая",
          "Плохая — жёсткая, режет нож",
          "Привкус желатина — брак",
          "Кули рядом должен быть свежим, не из банки"
        ]
      }
    ],
    "cta": { "text": "Забронировать стол с десертами", "href": "/" }
  },
  {
    "slug": "chto-zakazat-na-pervoe-svidanie-restoran",
    "title": "Что заказать на первое свидание в ресторане: советы и стратегия",
    "metaTitle": "Что заказать на первое свидание в ресторане | Паруса на крыше",
    "metaDescription": "Рекомендации по выбору блюд на первом свидании в ресторане. Что не стоит заказывать, как не попасть в ловушки меню и произвести правильное впечатление.",
    "keywords": "что заказать на первое свидание ресторан, первое свидание меню, блюда для свидания ресторан",
    "date": "2026-06-07",
    "category": "Кухня и еда",
    "readTime": 5,
    "image": "/photos/interior.webp",
    "lead": "Первое свидание в ресторане — это испытание не только нервов, но и меню. Что заказать, чтобы было вкусно, удобно есть и не создать неловкой ситуации? Несколько честных советов от практики.",
    "sections": [
      {
        "heading": "Что лучше не заказывать",
        "body": "Спагетти и длинная паста — соус летит. Ребра и блюда с костями — неудобно. Лобстер целиком — красивый, но есть его без опыта сложно. Очень острые блюда — если вы не знаете реакцию организма спутника. Чесночный хлеб в начале — запах остаётся надолго. Это не строгие правила, а здравый смысл: еда на первом свидании должна быть фоном, а не главным героем.",
        "list": [
          "Длинная паста — соус разлетается",
          "Кости и ребра — неудобно и неопрятно",
          "Очень острое — непредсказуемая реакция",
          "Лобстер целиком — сложно есть без навыка"
        ]
      },
      {
        "heading": "Хорошие варианты для первого свидания",
        "body": "Карпаччо или тартар — элегантно, легко есть, интересно обсуждать. Ризотто — удобно, делится мнениями. Стейк или рыба с гарниром — классика, нет неловкостей. Тарелка сыров или мясная нарезка на двоих — отличный повод делить еду и разговаривать. Десерт на двоих — классика финала вечера. В «Парусах на крыше» меню построено так, что большинство блюд удобны для совместной трапезы, а виды на город создают атмосферу сами по себе.",
        "highlight": "Лучший выбор — то, что вы точно умеете есть и что вам нравится. Уверенность в заказе лучше любого дорогого блюда."
      },
      {
        "heading": "Вино и напитки на первом свидании",
        "body": "Бутылка вина на двоих — хорошая идея, если вы оба пьёте. Спросите сомелье о паре к вашим блюдам — это и практично, и показывает вкус. Если не знаете вин — просто скажите официанту бюджет и вкусовые предпочтения (сухое/сладкое, лёгкое/насыщенное). Вам помогут. Игристое в начале — шампанское или просекко — задаёт правильный тон. В «Парусах» есть хорошая карта вин и живая музыка — это создаёт нужную атмосферу без лишних усилий.",
        "list": [
          "Игристое на старт — классика",
          "Спросите сомелье о паре к блюдам",
          "Не нужно брать самое дорогое",
          "Вода обязательна — чтобы не торопиться"
        ]
      }
    ],
    "cta": { "text": "Забронировать романтический стол", "href": "/romanticheskiy-uzhin" }
  },
  {
    "slug": "ustritsy-kak-est-restoran",
    "title": "Устрицы в ресторане: как их есть, с чем сочетать и на что обращать внимание",
    "metaTitle": "Устрицы в ресторане — как есть и что выбрать | Паруса на крыше",
    "metaDescription": "Полный гид по устрицам в ресторане: как есть устрицы, с чем подают, как выбрать свежие и что нужно знать о видах и происхождении.",
    "keywords": "устрицы ресторан как есть, устрицы петербург ресторан, как выбрать устрицы меню",
    "date": "2026-06-08",
    "category": "Кухня и еда",
    "readTime": 6,
    "image": "/photos/food.webp",
    "lead": "Устрицы — символ гастрономической культуры. Их едят во всём мире, но правила употребления знают далеко не все. Если вы ни разу не пробовали или только начинаете знакомство — этот гид поможет не растеряться в ресторане.",
    "sections": [
      {
        "heading": "Что нужно знать об устрицах",
        "body": "Устрицы бывают плоские (Ostrea edulis, европейская) и глубокие (Crassostrea gigas, тихоокеанская). Плоские — более редкие, с металлическим, йодистым вкусом. Глубокие — более распространены, вкус более мягкий, молочный, с морским ароматом. Размер обозначается цифрами: у глубоких устриц — от №5 (маленькие) до №0 (очень большие). Небольшие устрицы (№3–4) часто вкуснее: они нежнее и концентрированнее по вкусу.",
        "list": [
          "Плоские — редкие, металлический вкус",
          "Глубокие — обычные в ресторанах, мягкие",
          "Размер №3–4 — оптимальный для первого знакомства",
          "Происхождение: Нормандия, Марен, Бретань — лучшие"
        ]
      },
      {
        "heading": "Как едят устрицы",
        "body": "Устрицы подают на льду открытыми. Нужно взять устрицу в руку, добавить лимонный сок (он «проверяет» свежесть — живая устрица слегка сожмётся) и выпить содержимое, подчерпнув устричный ликёр (жидкость внутри). Жевать или нет — личное дело. Классика: не жевать, проглотить. Современный подход: пожевать несколько раз для вкуса. К устрицам традиционно подают лимон, уксус с луком-шалот, хлеб с маслом. Напитки: шабли, мюскаде, шампанское.",
        "highlight": "Капните лимон на устрицу — если она чуть сжалась, она живая и свежая. Если нет реакции — лучше не есть."
      },
      {
        "heading": "Как заказать в ресторане",
        "body": "Заказывайте по 6 штук на начало — это стандартная порция для знакомства. Если нравятся — берите ещё. Спросите официанта о происхождении: Нормандия, Бретань, Марен-Олерон — лучшие французские регионы. Сезон устриц: традиционно их едят в месяцы с буквой «р» (сентябрь–апрель), хотя современные устричные фермы дают продукт круглый год. Летом устрицы могут быть менее жирными и более «молочными».",
        "list": [
          "Стандартная порция: 6 штук",
          "Лучшее происхождение: Нормандия, Бретань",
          "Вино: шабли или шампанское",
          "Зимой и осенью — самые жирные и вкусные"
        ]
      }
    ],
    "cta": { "text": "Забронировать стол", "href": "/" }
  },
  {
    "slug": "ryba-restoran-kak-vybrat-blyudo",
    "title": "Как выбрать рыбное блюдо в ресторане: советы и ориентиры",
    "metaTitle": "Как выбрать рыбное блюдо в ресторане | Паруса на крыше",
    "metaDescription": "Гид по выбору рыбных блюд в ресторане: какую рыбу брать, что значит свежая рыба, как выбрать по меню и не ошибиться.",
    "keywords": "рыбное блюдо ресторан как выбрать, рыба в ресторане петербург, свежая рыба меню",
    "date": "2026-06-08",
    "category": "Кухня и еда",
    "readTime": 6,
    "image": "/photos/food.webp",
    "lead": "Рыба в ресторане — это одновременно самое вкусное и самое рискованное. Отличная рыба — деликатес. Несвежая или плохо приготовленная — разочарование. Как не ошибиться при выборе?",
    "sections": [
      {
        "heading": "Признаки хорошей рыбы в меню",
        "body": "Ресторан, который работает со свежей рыбой, обычно указывает происхождение: «семга мурманская», «судак ладожский», «дорадо из Греции». Если в меню просто написано «рыба» без уточнения — это сигнал. Также хороший знак — если позиции с рыбой меняются по сезону. В Петербурге рыбная кухня традиционно сильна: близость к Карелии и Финскому заливу даёт доступ к свежей форели, судаку, лещу, корюшке.",
        "list": [
          "Указано происхождение — хороший знак",
          "Меню меняется сезонно — значит работают со свежим",
          "Корюшка в сезон (весна) — петербургская классика",
          "Спросите об уловах недели — живые рестораны отвечают"
        ]
      },
      {
        "heading": "Виды рыбных блюд и техники",
        "body": "Рыба на гриле — чистый вкус, быстро готовится, хорошо показывает качество продукта. Рыба конфи — нежная, тающая, долгое томление. Рыба в соляной корке — сохраняет сочность, эффектная подача. Уха — концентрат вкуса, показатель работы с костями и головами. Рыбный тартар — из сёмги или тунца, сырой, нежный. В «Парусах на крыше» рыбные блюда готовятся с учётом того, что поступило от поставщика в этот день.",
        "highlight": "Лучший способ оценить ресторан: заказать рыбу на гриле без соусов. Если рыба вкусная сама по себе — кухня хорошая."
      },
      {
        "heading": "Рыба и вино",
        "body": "Классическое правило: рыба + белое вино. Но есть нюансы. Нежная рыба (камбала, судак) — лёгкие белые, пино гри, шардоне без выдержки. Жирная рыба (сёмга, тунец) — полнотелое белое или даже лёгкое красное (пино нуар). Рыба с соусом из томатов или специй — розовое или лёгкое красное. В «Парусах» сомелье поможет выбрать пару к вашему блюду — просто скажите, что заказали.",
        "list": [
          "Нежная рыба: пино гри, шардоне",
          "Лосось: полнотелое белое или лёгкое красное",
          "Рыба с пряным соусом: розовое вино",
          "Всегда можно спросить сомелье"
        ]
      }
    ],
    "cta": { "text": "Посмотреть меню", "href": "/menu" }
  },
  {
    "slug": "pasta-karbonara-klassika",
    "title": "Паста карбонара: история, классический рецепт и где найти настоящую",
    "metaTitle": "Паста карбонара — классика и история | Паруса на крыше",
    "metaDescription": "История карбонары, классический рецепт без сливок, почему в России её часто портят и как найти настоящую карбонару в ресторане.",
    "keywords": "паста карбонара классика, карбонара без сливок рецепт, настоящая карбонара ресторан",
    "date": "2026-06-08",
    "category": "Кухня и еда",
    "readTime": 5,
    "image": "/photos/food.webp",
    "lead": "Паста карбонара — одно из самых обсуждаемых блюд итальянской кухни. Особенно в России, где её чаще всего готовят неправильно. «Ужасная ересь» — так итальянские повара называют версию со сливками. Но что же такое настоящая карбонара?",
    "sections": [
      {
        "heading": "История блюда",
        "body": "Карбонара появилась в Риме и Лацио в середине XX века. Версий об её происхождении несколько: от блюда угольщиков (carbonari) до послевоенной импровизации с американским беконом. Название действительно связывают с carbonaro — угольщиком: крупно молотый перец на белом соусе напоминает угольную пыль. Первое документальное упоминание датируется 1954 годом в итальянских книгах рецептов.",
        "list": [
          "Родина: Рим, Лацио",
          "Дата появления: середина XX века",
          "Классические ингредиенты: гуанчале, пекорино романо, яйца, перец",
          "Сливки — не итальянская традиция, добавлены позже"
        ]
      },
      {
        "heading": "Классический рецепт",
        "body": "Настоящая карбонара: паста (спагетти или риgatoni), гуанчале (вяленые свиные щёки) или панчетта, яичные желтки + целые яйца, пекорино романо (или пармезан), чёрный перец крупного помола. Никаких сливок, чеснока, петрушки. Соус создаётся за счёт яиц и жира от гуанчале, смешанных с крахмалистой водой от пасты. Именно поэтому хорошая карбонара шёлковая, а не жидкая и не свёрнувшаяся.",
        "highlight": "Главная ошибка: добавлять яйца в горячую сковороду — белок сворачивается. Надо снять с огня и вмешивать яйца в остывшую пасту."
      },
      {
        "heading": "Где найти хорошую карбонару",
        "body": "В Петербурге итальянские рестораны делают карбонару по-разному. Признаки хорошей: соус кремовый, без хлопьев яйца, гуанчале или хотя бы панчетта (не бекон из магазина), пекорино (или смесь с пармезаном). Плохая карбонара: жидкая и водянистая или, наоборот, со сливками и загустителями. В «Парусах на крыше» паста готовится al dente и подаётся сразу — не хранится.",
        "list": [
          "Соус кремовый, без хлопьев",
          "Гуанчале или панчетта — не обычный бекон",
          "Паста al dente — с упругостью в центре",
          "Подаётся сразу, не разогревается"
        ]
      }
    ],
    "cta": { "text": "Посмотреть меню с пастой", "href": "/menu" }
  },
  {
    "slug": "sezonnoye-menyu-restoran-vesna",
    "title": "Сезонное меню в ресторане весной: что готовят и почему это важно",
    "metaTitle": "Сезонное меню весной в ресторане | Паруса на крыше",
    "metaDescription": "Что такое сезонное меню ресторана и почему весной оно особенное. Какие продукты появляются в марте–мае и что стоит пробовать в петербургских ресторанах.",
    "keywords": "сезонное меню ресторан весна, весенние блюда ресторан петербург, что едят весной ресторан",
    "date": "2026-06-09",
    "category": "Кухня и еда",
    "readTime": 5,
    "image": "/photos/terrace.webp",
    "lead": "Весна — лучшее время, чтобы ходить в рестораны. Не потому что стало теплее, хотя и это приятно. А потому что после зимы наконец-то появляются первые свежие продукты. Спаржа, черемша, радиккио, корюшка в Петербурге — всё это знаки весны на тарелке.",
    "sections": [
      {
        "heading": "Почему сезонное важнее стабильного",
        "body": "Ресторан с сезонным меню — это ресторан, который работает с живым продуктом. Помидор в июле и помидор в январе — разные продукты. Лосось в сезон нереста и лосось круглогодичный из аквакультуры — разный вкус. Шефы, работающие с сезоном, вынуждены постоянно придумывать новое — это держит кухню в тонусе. В «Парусах на крыше» меню обновляется с учётом сезона: весенние позиции появляются в марте–апреле.",
        "list": [
          "Спаржа: март–май — лучшая весенняя классика",
          "Черемша: апрель — петербургская традиция",
          "Корюшка: апрель–май — петербургский деликатес",
          "Молодой картофель: конец мая — первый урожай"
        ]
      },
      {
        "heading": "Корюшка — символ петербургской весны",
        "body": "Корюшка — маленькая рыбка с запахом огурца — главный весенний продукт Петербурга. Её ловят в Финском заливе с апреля по май. Традиционный способ приготовления — жарка в муке. В ресторанах её готовят иначе: конфи, маринованной, как тартар. Петербуржцы встречают корюшку как праздник — в городе даже проходит Праздник корюшки. Если вы в Петербурге весной — обязательно попробуйте.",
        "highlight": "Корюшка должна пахнуть свежим огурцом — это признак свежести. Если запах рыбный — она не первой свежести."
      },
      {
        "heading": "Что ещё характерно для весеннего меню",
        "body": "Весеннее меню в хорошем ресторане — это лёгкость: больше зелени, свежих трав, первых овощей. После зимних тушёных блюд и тяжёлых соусов хочется чего-то живого. Салаты с козьим сыром и молодыми листьями. Спаржа с голландским соусом. Ризотто с горошком. Рыба с зелёными травами. В «Парусах» весна — это также открытие террасы: с видом на весенний Петербург и блюдами из первых сезонных продуктов.",
        "list": [
          "Спаржа с голландским соусом — классика",
          "Салаты с молодыми листьями и козьим сыром",
          "Ризотто с горошком и мятой",
          "Лёгкие рыбные блюда с зелёными травами"
        ]
      }
    ],
    "cta": { "text": "Забронировать стол на террасе", "href": "/terrasa" }
  },
  {
    "slug": "veganskoe-menyu-restoran-trendy",
    "title": "Веганское меню в ресторане: что это, тренды 2026 и почему это вкусно",
    "metaTitle": "Веганское меню в ресторане — тренды 2026 | Паруса на крыше",
    "metaDescription": "Веганское меню в современных ресторанах: как оно изменилось, что стоит пробовать, почему это больше не скучно. Обзор веган-тенденций 2026 года.",
    "keywords": "веганское меню ресторан, веганские блюда петербург ресторан, вегетарианское меню тренды",
    "date": "2026-06-09",
    "category": "Кухня и еда",
    "readTime": 5,
    "image": "/photos/food.webp",
    "lead": "Ещё десять лет назад веганское меню в ресторане означало салат из огурцов и варёную гречку. Сегодня всё иначе. Лучшие рестораны мира предлагают веганские сеты, которые ничем не уступают мясным. Что изменилось?",
    "sections": [
      {
        "heading": "Как изменилось веганское меню",
        "body": "Революция произошла по нескольким причинам. Во-первых, шефы начали применять к растительным продуктам те же техники, что к мясу и рыбе: конфи из свёклы, тартар из авокадо, «стейк» из цветной капусты на гриле. Во-вторых, появились качественные заменители: аквафаба (вода от нута) вместо яичного белка, кешью-крем вместо сливок. В-третьих, фокус сместился на вкус и текстуру, а не на «это без животных».",
        "list": [
          "Конфи из свёклы — применение мясной техники к овощам",
          "Тартар из авокадо с томатами — популярный старт",
          "Стейк из цветной капусты на гриле — реальная альтернатива",
          "Аквафаба и кешью-крем — натуральные заменители"
        ]
      },
      {
        "heading": "Тренды веган-кухни 2026",
        "body": "Ферментация: кимчи, мисо, квашеные овощи — концентрат умами без мяса. Гриби как мясо: шиитаке, портобелло, вешенки в мясных техниках. Бобовые нового поколения: нут, чечевица, маш в изысканных подачах. Растительные «морепродукты»: водоросли вакаме, нори как основа умами-вкуса. В «Парусах» меню включает веганские опции в нескольких категориях — от закусок до десертов.",
        "highlight": "Лучшее веганское блюдо — то, которое вкусно само по себе, а не потому что «без мяса». Если шеф говорит «это лучший ризотто» — не важно, веганский он или нет."
      },
      {
        "heading": "Как заказать веганское в обычном ресторане",
        "body": "Большинство хороших ресторанов могут адаптировать блюдо под вегана, даже если это не прописано в меню. Спросите официанта: «Можно ли убрать сыр/масло/мясо из этого блюда?» В хорошем ресторане ответят «да» и предложат замену. Важно предупредить заранее, а не в момент подачи. В «Парусах» команда кухни готова к таким запросам — просто сообщите при бронировании.",
        "list": [
          "Предупредите при бронировании",
          "Спросите об адаптации блюд — хороший ресторан поможет",
          "Уточните состав соусов — часто там скрытые молочные",
          "Веганский десерт: сорбеты, шоколад без сливок"
        ]
      }
    ],
    "cta": { "text": "Забронировать стол", "href": "/" }
  },
  {
    "slug": "degustatsionnyy-set-restoran-chto-eto",
    "title": "Дегустационный сет в ресторане: что это, как работает и стоит ли заказывать",
    "metaTitle": "Дегустационный сет в ресторане — что это | Паруса на крыше",
    "metaDescription": "Что такое дегустационный сет (tasting menu), как он работает, сколько стоит, как долго идёт и стоит ли его заказывать. Полный гид для первого раза.",
    "keywords": "дегустационный сет ресторан, tasting menu что это, дегустационное меню петербург",
    "date": "2026-06-09",
    "category": "Кухня и еда",
    "readTime": 6,
    "image": "/photos/interior.webp",
    "lead": "Дегустационный сет — это не просто много маленьких блюд. Это рассказ шефа о своей кухне, своей философии, сезоне и территории. Формат становится всё популярнее в петербургских ресторанах. Но стоит ли его заказывать и что нужно знать заранее?",
    "sections": [
      {
        "heading": "Что такое дегустационный сет",
        "body": "Дегустационный сет (tasting menu) — последовательность из 5–12 и более небольших блюд, каждое из которых несёт определённую идею. Это альтернатива обычному меню «выбери сам». Шеф сам выбирает, что и в каком порядке вы попробуете. Порции маленькие — но их много, и в итоге вы наедаетесь. Формат позволяет увидеть весь диапазон кухни ресторана за один вечер.",
        "list": [
          "5–12 позиций минимум",
          "Шеф сам определяет последовательность",
          "Порции меньше обычных — но их много",
          "Часто с винным паррингом по блюдам"
        ]
      },
      {
        "heading": "Как долго длится и сколько стоит",
        "body": "Дегустационный сет — это не быстрый ужин. Средняя продолжительность: 2–3 часа, иногда дольше. Перерывы между блюдами дают возможность поговорить, оценить вкус, подготовиться к следующему. Стоимость в Петербурге: от 3 000 ₽ до 15 000+ ₽ за человека в зависимости от ресторана и количества позиций. Винный парринг добавляет ещё 30–50% к цене. Для особого вечера — это оправданные вложения.",
        "highlight": "Сет лучше заказывать в особый вечер, когда никуда не торопитесь. Это формат медленного ужина, а не рабочего обеда."
      },
      {
        "heading": "Стоит ли заказывать",
        "body": "Дегустационный сет стоит заказывать, если: вы хотите познакомиться с кухней ресторана глубоко; вы отмечаете что-то особенное; вы готовы потратить 2–3 часа за столом; вы открыты к неожиданным сочетаниям. Не стоит брать, если: вы очень голодны и хотите наесться быстро; у вас строгие диетарные ограничения (сообщите заранее); вы торопитесь. В «Парусах на крыше» опытная команда поможет выбрать оптимальный формат для вашего вечера.",
        "list": [
          "Подходит: особый вечер, знакомство с кухней",
          "Не подходит: если очень голодны или торопитесь",
          "Об ограничениях скажите при бронировании",
          "Берите с парингом вин — усиливает впечатление"
        ]
      }
    ],
    "cta": { "text": "Забронировать особый вечер", "href": "/" }
  },
  {
    "slug": "syry-restoran-kak-vybrat",
    "title": "Сыры в ресторане: как выбрать сырную тарелку и что к ней заказать",
    "metaTitle": "Сырная тарелка в ресторане — как выбрать | Паруса на крыше",
    "metaDescription": "Как выбрать сырную тарелку в ресторане, какие виды сыра бывают, что к ней подают и с каким вином сочетать. Полный гид для ценителей.",
    "keywords": "сырная тарелка ресторан, виды сыров ресторан меню, сыр и вино сочетание",
    "date": "2026-06-10",
    "category": "Кухня и еда",
    "readTime": 5,
    "image": "/photos/food.webp",
    "lead": "Сырная тарелка — один из лучших способов начать или закончить ужин. Хороший ассортимент сыров рассказывает о регионах, традициях, вкусах. Но в меню часто просто написано «сырная тарелка» без деталей. Что за этим скрывается?",
    "sections": [
      {
        "heading": "Типичный состав сырной тарелки",
        "body": "Хорошая сырная тарелка включает несколько типов: мягкий (бри, камамбер), полутвёрдый (гауда, грюйер), твёрдый (пармезан, грана падано), с плесенью (рокфор, горгонзола) и козий (шевр, роккевер). Это разнообразие текстур и вкусов — от мягкого до острого, от сладковатого до пикантного. К сырам обязательно подают: варенье (инжирное, виноградное), орехи, хлебцы или крекеры, а иногда мёд и хамон.",
        "list": [
          "Мягкий: бри, камамбер — нежный старт",
          "Твёрдый: пармезан, грюйер — насыщенный вкус",
          "С плесенью: рокфор — острый финал",
          "Козий: лёгкий, кислинка — разнообразие"
        ]
      },
      {
        "heading": "Сыр и вино",
        "body": "Классическое правило: красное вино и сыр. Но это упрощение. Красное вино часто перебивает нежные мягкие сыры. Лучшие сочетания: бри + шампанское, козий сыр + совиньон блан, пармезан + кьянти, рокфор + сотерн или портвейн. Правило «что растёт вместе, то и пьётся вместе» работает: нормандский камамбер хорош с нормандским сидром. В «Парусах» карта вин подбирается с учётом сырного ассортимента.",
        "highlight": "Рокфор и портвейн — одно из лучших сочетаний в мире. Сладость вина гасит соль и остроту сыра идеально."
      },
      {
        "heading": "Когда заказывать сыры",
        "body": "Во Франции сыры — самостоятельный курс между горячим и десертом. В остальном мире — часто закуска или замена горячему. Оба варианта правильные. Сырная тарелка как финал трапезы вместо десерта — отличная идея, особенно с портвейном или сотерном. Как стартер — хороша с игристым или белым вином. В «Парусах» сырная тарелка доступна в любой момент вечера — это гибкий формат.",
        "list": [
          "Как закуска: с игристым или белым вином",
          "Вместо десерта: с портвейном или сотерном",
          "Между горячим и десертом: французская традиция",
          "С мёдом и орехами — всегда правильно"
        ]
      }
    ],
    "cta": { "text": "Посмотреть меню", "href": "/menu" }
  },
  {
    "slug": "fermerskie-produkty-kukhnya-zachem",
    "title": "Фермерские продукты в ресторане: почему это важно и чем отличается",
    "metaTitle": "Фермерские продукты в ресторане — почему важно | Паруса на крыше",
    "metaDescription": "Что значит «фермерские продукты» в меню ресторана, чем они отличаются, как проверить подлинность и почему это влияет на вкус блюд.",
    "keywords": "фермерские продукты ресторан, локальные продукты кухня петербург, ресторан с фермерским меню спб",
    "date": "2026-06-10",
    "category": "Кухня и еда",
    "readTime": 5,
    "image": "/photos/food.webp",
    "lead": "«Фермерские продукты» в меню — это маркетинг или реальность? Иногда первое, иногда второе. Разбираем, что за этим стоит, как отличить настоящее от игры слов и почему локальный продукт действительно влияет на вкус.",
    "sections": [
      {
        "heading": "Что значит «фермерский» на самом деле",
        "body": "В России понятие «фермерский» не стандартизировано законодательно. Это значит, что любой ресторан может написать «фермерская курица» без реальной связи с конкретным фермером. Настоящий фермерский продукт — это когда ресторан может назвать ферму, её расположение и условия работы. Лучшие рестораны указывают «форель с фермы Радуга в Карелии» или «молоко от коровника в Ленинградской области». Это конкретика, а не маркетинг.",
        "list": [
          "Настоящее: указана конкретная ферма и регион",
          "Маркетинг: просто слово «фермерский» без уточнений",
          "Спросите официанта: откуда продукт",
          "Хороший ресторан ответит конкретно"
        ]
      },
      {
        "heading": "Почему это влияет на вкус",
        "body": "Продукт, выращенный рядом и быстро доставленный, объективно вкуснее. Помидор с теплицы в 50 км и созревший естественно vs помидор из Турции, сорванный зелёным — разные продукты. Рыба из Карелии, доставленная за день — и рыба из Чили, прошедшая заморозку — не сравнится. Это не снобизм — это физика и биохимия. Свежий продукт содержит больше вкусовых соединений и меньше потерял их при транспортировке.",
        "highlight": "Простой тест: если помидор пахнет помидором — он хороший. Если не пахнет — выращен промышленно."
      },
      {
        "heading": "Локальные продукты Петербурга и Северо-Запада",
        "body": "Петербург окружён богатейшей кулинарной базой. Карелия: форель, хариус, черника, брусника. Ленинградская область: молочные продукты, яйца, картофель, говядина. Финский залив: рыба и морепродукты. Псковская область: грибы, ягоды. В «Парусах на крыше» кухня ориентирована на локальное: меню строится вокруг того, что регион даёт в этот сезон. Это честная кухня.",
        "list": [
          "Карелия: форель, хариус, дикие ягоды",
          "Ленобласть: молоко, масло, говядина",
          "Финский залив: корюшка, навага, угорь",
          "Псков и Новгород: грибы, клюква, брусника"
        ]
      }
    ],
    "cta": { "text": "Забронировать стол", "href": "/" }
  },
  {
    "slug": "avtorskaya-kukhnya-shef-peterburg",
    "title": "Авторская кухня: что это такое и как её оценивать в ресторане",
    "metaTitle": "Авторская кухня в ресторане — что это и как оценивать | Паруса",
    "metaDescription": "Что такое авторская кухня шефа, в чём её отличие от традиционной, как её оценивать и почему Петербург — хорошее место для знакомства с ней.",
    "keywords": "авторская кухня ресторан петербург, что такое авторская кухня шефа, авторское меню ресторан спб",
    "date": "2026-06-10",
    "category": "Кухня и еда",
    "readTime": 6,
    "image": "/photos/interior.webp",
    "lead": "«Авторская кухня» — один из самых часто используемых и редко объясняемых терминов в ресторанном мире. Что это значит на деле, как понять, видите ли вы её перед собой — и почему это важно?",
    "sections": [
      {
        "heading": "Чем авторская кухня отличается от традиционной",
        "body": "Традиционная кухня воспроизводит проверенные рецепты: борщ, пасту болоньезе, ризотто грибное. Авторская кухня — это когда шеф берёт продукты, техники и опыт и создаёт что-то своё. Это не обязательно экстравагантно: авторская кухня может быть очень простой внешне, но с неожиданным сочетанием вкусов или продуктов. Признак авторской кухни: блюдо есть только в этом ресторане.",
        "list": [
          "Рецепт — собственный, не копия",
          "Продукты — выбраны шефом под концепцию",
          "Техники — могут быть традиционными или инновационными",
          "Меню меняется — шеф реагирует на сезон и вдохновение"
        ]
      },
      {
        "heading": "Как оценивать авторское блюдо",
        "body": "При оценке авторской кухни полезно задать себе несколько вопросов: Есть ли в блюде идея, которую я понимаю? Продукты сочетаются или удивляют намеренно? Технически блюдо выполнено — всё готово так, как задумано? Подача несёт смысл или это просто декор? В авторской кухне не всегда всё понятно с первого раза — и это нормально. Иногда нужно спросить официанта о замысле шефа.",
        "highlight": "Самый честный критерий: хочу ли я это съесть снова. Всё остальное — интерпретация."
      },
      {
        "heading": "Авторская кухня в Петербурге",
        "body": "Петербург — один из лидеров авторской кухни в России. Здесь много молодых шефов, получивших образование в Европе и вернувшихся применять знания с локальными продуктами. «Паруса на крыше» — один из примеров: кухня сочетает европейские техники с петербургскими продуктами и историей. Ул. Марата, 5, 6-й этаж Olympic Plaza. Работаем с 2009 года. Рейтинг 5,0, 2 606 отзывов на Яндексе.",
        "list": [
          "Петербург — центр российской авторской гастрономии",
          "Много шефов с европейским образованием",
          "Локальные продукты + мировые техники",
          "«Паруса» работают с 2009 года в этой традиции"
        ]
      }
    ],
    "cta": { "text": "Забронировать стол", "href": "/" }
  },
  {
    "slug": "bezglyutenovoe-menyu-restoran",
    "title": "Безглютеновое меню в ресторане: что искать и как правильно заказывать",
    "metaTitle": "Безглютеновое меню в ресторане | Паруса на крыше",
    "metaDescription": "Как найти безглютеновые блюда в ресторане, что нужно уточнить при заказе и как рестораны адаптируют меню для гостей с непереносимостью глютена.",
    "keywords": "безглютеновое меню ресторан, глютен ресторан что избегать, безглютеновая диета ресторан петербург",
    "date": "2026-06-11",
    "category": "Кухня и еда",
    "readTime": 5,
    "image": "/photos/food.webp",
    "lead": "Глютен (клейковина) — белок пшеницы, ржи и ячменя. Для людей с целиакией или чувствительностью к глютену его употребление вызывает серьёзные проблемы. Как ориентироваться в ресторанном меню и что нужно знать, чтобы поужинать безопасно?",
    "sections": [
      {
        "heading": "Где скрывается глютен",
        "body": "Очевидные источники: хлеб, паста, пицца. Неочевидные: соевый соус (часто содержит пшеницу), соусы-загустители (ру на муке), панировка, маринады, пиво. Даже «простое» блюдо — жареная рыба — может содержать глютен, если рыбу обваливают в муке. Важно спрашивать не только о составе блюда, но и о технике приготовления. Кросс-контаминация (пересечение) — тоже риск: одна сковорода для рыбы в муке и для «чистого» стейка.",
        "list": [
          "Соевый соус: часто содержит пшеницу",
          "Панировка: всегда спрашивайте",
          "Загустители соусов: могут быть на муке",
          "Кросс-контаминация: отдельная посуда важна"
        ]
      },
      {
        "heading": "Что безопасно в ресторане",
        "body": "Безглютеновые продукты: мясо без маринада и панировки, рыба без панировки, овощи без соусов, рис и гречка, большинство натуральных продуктов. Стейк на гриле — обычно безопасен. Ризотто из риса — безопасно, если нет загустителей. Салаты без гренок и с заправкой из масла и лимона — отлично. Десерты: сорбеты и крем-брюле обычно без глютена. Тирамису — нет (савоярди из пшеницы).",
        "highlight": "Скажите официанту чётко: «У меня целиакия» или «Я не могу есть глютен совсем». Хороший ресторан воспримет это серьёзно и проверит с кухней."
      },
      {
        "heading": "Как заказывать",
        "body": "Предупредите заранее — при бронировании или в начале вечера. Попросите официанта описать состав блюда и технику. В «Парусах на крыше» команда готова к таким запросам: при бронировании укажите диетарные ограничения, и кухня подготовится. Мы не гарантируем полное отсутствие кросс-контаминации (кухня общая), но максимально стараемся минимизировать риски для гостей с пищевыми ограничениями.",
        "list": [
          "Предупредите при бронировании",
          "Назовите «целиакия» — это серьёзнее звучит",
          "Спросите об отдельной посуде",
          "Безопасные позиции: стейк, рыба без панировки, салаты"
        ]
      }
    ],
    "cta": { "text": "Забронировать стол", "href": "/" }
  },
  {
    "slug": "chto-takoe-paring-vina-i-edy-restoran",
    "title": "Что такое парринг вина и еды: принципы сочетания для новичков",
    "metaTitle": "Парринг вина и еды — принципы сочетания | Паруса на крыше",
    "metaDescription": "Что такое парринг (wine pairing), основные принципы сочетания вина с едой, самые популярные пары и как выбрать вино к блюду в ресторане.",
    "keywords": "парринг вина и еды, wine pairing принципы, вино к блюду как выбрать ресторан",
    "date": "2026-06-11",
    "category": "Кухня и еда",
    "readTime": 6,
    "image": "/photos/interior.webp",
    "lead": "Парринг (от английского pairing — «пара, сочетание») — искусство подбора вина к блюду так, чтобы они усиливали, а не нейтрализовывали вкусы друг друга. Это можно изучать годами — или освоить несколько базовых принципов и сразу получать удовольствие.",
    "sections": [
      {
        "heading": "Базовые принципы паррринга",
        "body": "Первый принцип: региональное сочетание. Что растёт вместе — пьётся вместе. Пармская ветчина + просекко. Бургундское бёф-бургиньон + бургундское пино нуар. Тосканская рибба + кьянти. Второй: баланс веса. Лёгкое блюдо (рыба, салат) — лёгкое вино. Тяжёлое (жирное мясо, дичь) — полнотелое. Третий: контраст или усиление. Сладкое вино + солёный сыр (контраст). Красное + говядина (усиление умами).",
        "list": [
          "Региональный принцип: что растёт вместе — пьётся вместе",
          "Балансируйте «вес» блюда и вина",
          "Контраст: сладкое + солёное работает",
          "Усиление: умами мяса + танины красного"
        ]
      },
      {
        "heading": "Популярные пары",
        "body": "Классические сочетания, которые работают всегда: рыба + шабли или мюскаде; устрицы + шампанское; козий сыр + совиньон блан; говяжий стейк + каберне совиньон или мальбек; утиная грудка + пино нуар; голубой сыр + сотерн или портвейн; шоколадный десерт + амаретто или педро хименес. Это не священные правила — а отправные точки. Лучший парринг — тот, который нравится вам.",
        "highlight": "Правило «белое к рыбе, красное к мясу» — упрощение. Лосось с ягодным соусом может требовать пино нуар. Телятина — белого бургундского."
      },
      {
        "heading": "Как выбрать вино в ресторане",
        "body": "Самый простой путь: спросите сомелье или официанта. Скажите, что заказали, и свой бюджет — вам подберут. Не стесняйтесь: это их работа, и хороший ресторан гордится картой вин. В «Парусах на крыше» карта вин составлена так, чтобы к каждому разделу меню был свой вариант. Диапазон цен позволяет выбрать от доступного до премиального. Парринг по блюдам доступен при заказе дегустационного сета.",
        "list": [
          "Спросите сомелье — это его работа",
          "Назовите бюджет честно",
          "Скажите, что любите: сухое/сладкое, лёгкое/насыщенное",
          "Экспериментируйте: лучший парринг найдёте сами"
        ]
      }
    ],
    "cta": { "text": "Забронировать стол", "href": "/" }
  },
  {
    "slug": "kalyannaya-karta-restoran-chto-zakaz",
    "title": "Кальян в ресторане: что нужно знать при первом заказе",
    "metaTitle": "Кальян в ресторане — что нужно знать | Паруса на крыше",
    "metaDescription": "Как правильно выбрать и заказать кальян в ресторане: виды табака, крепость, сочетание с едой и вопросы, которые стоит задать кальянному мастеру.",
    "keywords": "кальян ресторан что заказать, кальян табак крепость, кальян меню как выбрать",
    "date": "2026-06-11",
    "category": "Кухня и еда",
    "readTime": 5,
    "image": "/photos/terrace.webp",
    "lead": "Кальян в ресторане — это отдельная культура. Выбор табака, крепость, вкус, время подачи — всё имеет значение. Если вы заказываете кальян впервые или хотите сделать это правильно, вот несколько ориентиров.",
    "sections": [
      {
        "heading": "Виды табака и вкусов",
        "body": "Кальянный табак делится на два основных типа: влажный (Hon Tobacco, Tangiers) — более насыщенный, для опытных; влажный с гликолем (Al Fakher, Fumari) — мягкий, популярный. По вкусам: фруктовые (яблоко, манго, персик), ягодные (арбуз, виноград, черника), ментол, мятные, цветочные, смешанные. Для первого раза рекомендуется что-то лёгкое и фруктовое: яблоко с мятой или персик.",
        "list": [
          "Для первого раза: яблоко, персик, виноград",
          "Мята и ментол: освежает, хорошо летом",
          "Ягоды: сладкие, популярны среди женщин",
          "Крепкий: только для опытных"
        ]
      },
      {
        "heading": "Крепость и время",
        "body": "Крепость кальяна зависит от табака и температуры углей. Лёгкий кальян — около 45 минут. Полноценный — 1–1,5 часа. Кальян лучше заказывать после еды: на пустой желудок он действует сильнее. Не торопитесь с затяжками: кальян — медленное удовольствие, один раз в 30–60 секунд. В «Парусах» кальян подаётся с терпеливым сервисом — мастер настраивает и следит за угольным жаром.",
        "highlight": "Заказывайте кальян после еды, не на голодный желудок. Так впечатление будет приятным, а не неожиданным."
      },
      {
        "heading": "Кальян и вечер в ресторане",
        "body": "Кальян хорошо сочетается с медленным вечером: живая музыка, хороший напиток, неспешный разговор. В «Парусах на крыше» кальян доступен на террасе с видом на Петербург. Это создаёт особую атмосферу: дым уходит вверх, под ним — огни города. Для компании кальян — отличный способ завершить вечер или сделать его более расслабленным. Заранее уточните наличие при бронировании.",
        "list": [
          "После еды — самое правильное время",
          "Подходит для медленного вечера с компанией",
          "Хорошо с легким алкоголем или чаем",
          "Уточните наличие при бронировании"
        ]
      }
    ],
    "cta": { "text": "Забронировать стол с кальяном", "href": "/" }
  },
  {
    "slug": "igristoe-vino-dlya-prazdnika-vybor",
    "title": "Игристое вино для праздника: шампанское, просекко или кава — что выбрать",
    "metaTitle": "Игристое вино для праздника — шампанское или просекко | Паруса",
    "metaDescription": "Как выбрать игристое вино для праздника: разница между шампанским, просекко и кавой, когда что уместно и как правильно подавать.",
    "keywords": "игристое вино праздник, шампанское просекко разница, кава игристое что выбрать",
    "date": "2026-06-12",
    "category": "Кухня и еда",
    "readTime": 5,
    "image": "/photos/view_sunset.webp",
    "lead": "Игристое вино — неотъемлемая часть праздника. Но что выбрать: шампанское, просекко, каву или крémant? В чём разница и когда что уместно? Разбираем без снобизма.",
    "sections": [
      {
        "heading": "Шампанское: что делает его особенным",
        "body": "Шампанское — только из Шампани (Франция) из сортов шардоне, пино нуар и пино менье. Метод производства — традиционный (вторичное брожение в бутылке). Результат: тонкие пузырьки, сложный вкус с нотами хлеба, дрожжей, цитруса. Это вершина игристых вин — и самое дорогое. Для особых поводов: свадьба, юбилей, предложение руки. Средняя цена в ресторане: от 5 000 ₽ за бутылку.",
        "list": [
          "Только из Шампани, Франция",
          "Метод: традиционный (в бутылке)",
          "Вкус: сложный, с хлебными и дрожжевыми нотами",
          "Для: самых особых поводов"
        ]
      },
      {
        "heading": "Просекко и кава",
        "body": "Просекко — итальянское игристое из винограда Глера. Метод шарма (в цистерне) — более простое производство. Вкус: фруктовый, цветочный, лёгкий. Цена ниже шампанского. Отлично для аперитива, первого свидания, вечеринки в непринуждённой атмосфере. Кава — испанское игристое, производится традиционным методом как шампанское, но из других сортов. Более «хлебный» вкус, хорошее соотношение цена/качество. Хороший выбор для дружеского ужина.",
        "highlight": "Просекко — для лёгкости и удовольствия. Шампанское — для торжества. Кава — отличный компромисс по цене и сложности."
      },
      {
        "heading": "Как подавать игристое",
        "body": "Температура подачи: 6–8°C — холодное, но не ледяное. Бокал: флюте (длинный узкий) или кубок (широкий) для шампанского. Флюте лучше показывает пузырьки и удерживает аромат. Открывать: медленно, придерживая пробку, без хлопка (хлопок — потеря давления и половина вина). В «Парусах» игристое подаётся в правильных бокалах при нужной температуре — не нужно об этом беспокоиться.",
        "list": [
          "Температура: 6–8°C, не ледяное",
          "Бокал: флюте — лучший для аромата",
          "Открывать: медленно, без хлопка",
          "Хранить: на боку в прохладном месте"
        ]
      }
    ],
    "cta": { "text": "Забронировать праздничный стол", "href": "/" }
  },
  {
    "slug": "zavtrak-v-restorane-peterbug",
    "title": "Завтрак в ресторане Петербурга: что стоит знать и куда идти",
    "metaTitle": "Завтрак в ресторане Петербурга | Паруса на крыше",
    "metaDescription": "Где позавтракать в ресторане Санкт-Петербурга, что обычно включают в завтрак и почему завтрак с видом — лучшее начало дня в Петербурге.",
    "keywords": "завтрак ресторан петербург, завтрак с видом на петербург, где позавтракать центр спб",
    "date": "2026-06-12",
    "category": "Кухня и еда",
    "readTime": 5,
    "image": "/photos/view_day.webp",
    "lead": "Завтрак в Петербурге — дело особое. Утренний свет над крышами, запах кофе, первые виды на Казанский собор или Неву. В этот момент никакой спешки не хочется. Разбираем, как выбрать ресторан для завтрака и что ожидать.",
    "sections": [
      {
        "heading": "Что бывает на завтраке в ресторане",
        "body": "Классический формат: континентальный завтрак (круассаны, хлеб, масло, варенье, йогурт, фруктовый сок, кофе). Расширенный: яйца различных видов (омлет, бенедикт, пашот), каши, мясная нарезка, сыры. Авторский завтрак: то, что придумал шеф — сезонные продукты, локальные акценты. В «Парусах на крыше» завтрак начинается с 10:00 — именно в то время, когда утренний Петербург наиболее красив с 6-го этажа.",
        "list": [
          "Континентальный: хлеб, масло, кофе — базовый",
          "Расширенный: яйца, сыры, мясное",
          "Авторский: то, что придумал шеф",
          "Время: с 10:00 в «Парусах»"
        ]
      },
      {
        "heading": "Завтрак с видом на Петербург",
        "body": "Утренний Петербург особенно красив. Свет мягкий, туристов меньше, город кажется личным. С 6-го этажа Olympic Plaza видны купола и крыши центрального района — именно то, что хочется видеть за первым кофе. Этот вид с утра задаёт тон всему дню. Паруса на крыше — хорошая точка для такого начала: тихо, не суетно, с живым городом в окне.",
        "highlight": "В белые ночи (с мая по июль) завтрак в «Парусах» — это отдельный опыт: светло в любое время, и город выглядит совсем иначе."
      },
      {
        "heading": "Практические советы",
        "body": "Забронируйте стол заранее, особенно в выходные — завтрак у окна с видом разбирают быстро. Хорошо совместить с прогулкой: завтрак в «Парусах», потом пешком на Невский или к Казанскому собору — 10 минут. Кофе попросите принести сразу — это задаёт правильный тон. Если планируете длинное утро — возьмите авторский завтрак, а не экспресс. В «Парусах» никто не торопит.",
        "list": [
          "Бронируйте стол заранее, особенно у окна",
          "Совместите с утренней прогулкой по центру",
          "Попросите кофе сразу",
          "Авторский завтрак — для неспешного утра"
        ]
      }
    ],
    "cta": { "text": "Забронировать завтрак", "href": "/" }
  },
  {
    "slug": "chay-restoran-kak-vybrat-kartu",
    "title": "Чайная карта в ресторане: как разобраться и что стоит пробовать",
    "metaTitle": "Чайная карта в ресторане — как выбрать | Паруса на крыше",
    "metaDescription": "Как разобраться в чайной карте ресторана, виды чая, температура заваривания, правила выбора и с чем сочетать. Гид для любителей чая.",
    "keywords": "чайная карта ресторан, как выбрать чай ресторан, виды чая меню ресторан",
    "date": "2026-06-12",
    "category": "Кухня и еда",
    "readTime": 5,
    "image": "/photos/interior.webp",
    "lead": "Хорошая чайная карта в ресторане — такой же знак качества, как карта вин. Всё больше ресторанов уделяют внимание выбору чая: не только зелёный/чёрный/травяной, но и конкретные сорта, регион, год урожая. Как в этом разобраться?",
    "sections": [
      {
        "heading": "Основные виды чая",
        "body": "Зелёный: не окисленный, свежий вкус, много вариаций (сенча, матча, лун цзин). Температура заваривания: 70–80°C. Белый: минимально обработанный, нежный, сладковатый. Температура: 70–75°C. Улун: частично окисленный, широкий диапазон от зелёного к красному. Температура: 80–95°C. Чёрный: полностью окисленный, насыщенный (ассам, дарджилинг, эрл грей). Температура: 95–100°C. Пуэр: ферментированный, землистый, с умами.",
        "list": [
          "Зелёный: 70–80°C, свежий вкус",
          "Улун: 80–95°C, разнообразный",
          "Чёрный: 95–100°C, насыщенный",
          "Пуэр: ферментированный, для опытных"
        ]
      },
      {
        "heading": "На что обращать внимание в меню",
        "body": "Хорошая чайная карта указывает: регион (Дарджилинг, Фуцзянь, Асам), иногда плантацию или год урожая (для премиального чая). Если в меню просто «зелёный» без уточнений — это купажированный чай из пакетика, скорее всего. Если указан «сенча из Кагосимы» — значит ресторан работает со специализированным поставщиком. В «Парусах» карта напитков включает как классику, так и специальные позиции.",
        "highlight": "Лучший чай заваривается в отдельном чайнике с нужной температурой воды — не кипятком. Попросите, если видите, что несут кипяток к зелёному чаю."
      },
      {
        "heading": "С чем сочетать",
        "body": "Зелёный чай: лёгкие десерты (сорбеты, маринованные фрукты), суши, рыбные блюда. Чёрный чай: сырные тарелки, шоколадные десерты, тосты с джемом. Улун: выпечка, фрукты, белый шоколад. Пуэр: жирные блюда (утка, баранина) — он «разрезает» жирность. В «Парусах» официант может порекомендовать чай к вашему десерту или горячему — просто спросите.",
        "list": [
          "Зелёный + лёгкие десерты и рыба",
          "Чёрный + шоколад и сыры",
          "Улун + выпечка и фрукты",
          "Пуэр + жирные блюда"
        ]
      }
    ],
    "cta": { "text": "Посмотреть карту напитков", "href": "/menu" }
  },
  {
    "slug": "gazpasho-kholodnyy-sup-restoran",
    "title": "Гаспачо: холодный суп в ресторане — история, виды и как его оценивать",
    "metaTitle": "Гаспачо в ресторане — история и виды холодного супа | Паруса",
    "metaDescription": "Что такое гаспачо, откуда он родом, какие виды бывают и почему хорошее гаспачо — признак летней кухни в ресторане.",
    "keywords": "гаспачо ресторан что это, холодный суп ресторан, гаспачо петербург меню",
    "date": "2026-06-13",
    "category": "Кухня и еда",
    "readTime": 5,
    "image": "/photos/food.webp",
    "lead": "Гаспачо — холодный томатный суп из Андалусии (Испания). Летом это один из самых освежающих способов начать обед. В хороших ресторанах его готовят из спелых томатов, перца, огурца, чеснока и оливкового масла — никаких консервов и пакетов.",
    "sections": [
      {
        "heading": "Что такое гаспачо",
        "body": "Классический андалузский гаспачо: томаты, огурец, красный перец, чеснок, лук, оливковое масло первого отжима, уксус хересный, хлеб (для загустения), соль. Всё пробивается блендером и охлаждается несколько часов. Подаётся в чашке или тарелке с топпингами: кубиками хлеба, огурца, перца. Вкус: свежий, кислый, с лёгкой остротой от чеснока.",
        "list": [
          "Основа: свежие томаты + огурец + перец",
          "Связка: хлеб, оливковое масло, уксус",
          "Подаётся: холодным, с топпингами",
          "Вкус: свежий, кислый, с чесноком"
        ]
      },
      {
        "heading": "Виды гаспачо",
        "body": "Белый гаспачо (aho blanco): миндаль, чеснок, хлеб, оливковое масло, виноград. Зелёный: с авокадо, шпинатом, огурцом. Клубничный: модная летняя версия с клубникой и уксусом. Арбузный: арбуз + томат + мята — очень летний. В ресторанах авторские версии гаспачо часто появляются в сезонном меню: шеф берёт принцип (холодный пюрированный суп) и применяет к сезонным продуктам.",
        "highlight": "Хорошее гаспачо должно пахнуть свежими томатами — это главный критерий. Если запах консервный — суп сделан из банки."
      },
      {
        "heading": "Как оценить в ресторане",
        "body": "Признаки хорошего гаспачо: насыщенный томатный цвет, свежий аромат, баланс кислотности и сладости, однородная консистенция без крупных кусков. Топпинги должны быть свежими и хрустящими. Плохое гаспачо: водянистое, безвкусное, с пластиковым запахом — такое бывает при использовании тепличных зимних томатов. Идеальный сезон для гаспачо в ресторане — июль-август, когда томаты спелые. В «Парусах» холодные супы появляются в летнем меню.",
        "list": [
          "Свежий томатный запах — главный признак",
          "Баланс кислоты и сладости",
          "Топпинги хрустящие, свежие",
          "Лучший сезон: июль-август"
        ]
      }
    ],
    "cta": { "text": "Посмотреть летнее меню", "href": "/menu" }
  },
  {
    "slug": "steyky-v-restorane-stepeni-prochozhki",
    "title": "Стейки в ресторане: степени прожарки и как выбрать правильно",
    "metaTitle": "Стейки в ресторане — степени прожарки и выбор | Паруса на крыше",
    "metaDescription": "Всё о стейках в ресторане: степени прожарки (rare, medium, well done), виды стейков, как выбрать и что ожидать от хорошего стейка.",
    "keywords": "стейк ресторан степени прожарки, стейк петербург ресторан, виды стейков меню",
    "date": "2026-06-13",
    "category": "Кухня и еда",
    "readTime": 7,
    "image": "/photos/food.webp",
    "lead": "Стейк — классика ресторанного меню, которая требует понимания. Степень прожарки, вид стейка, порода скота — всё это влияет на результат. Разбираем, как не ошибиться при заказе и получить то, что хотели.",
    "sections": [
      {
        "heading": "Степени прожарки стейка",
        "body": "Blue (Raw) — почти сырое, только слегка запечатанное снаружи. Внутри красное, едва тёплое. Rare — красный центр, тёплый. Medium Rare — розовый центр, оптимальная прожарка для большинства стейков. Medium — розовый центр с серым ободком, умеренно. Medium Well — серое с лёгкой розовинкой. Well Done — полностью серое, без розового. Большинство шефов рекомендуют medium rare: сок остаётся в мясе, текстура оптимальная.",
        "list": [
          "Rare: красный центр, тёплый",
          "Medium Rare: розовый, сочный — рекомендуется",
          "Medium: розово-серый",
          "Well Done: полностью прожаренный, суше"
        ]
      },
      {
        "heading": "Виды стейков",
        "body": "Рибай — из рёберной части, много мраморности, насыщенный вкус. Стриплойн (NY strip) — менее жирный, более плотный. Филе миньон — из вырезки, самый нежный, наименее жирный. Ти-бон — комбинирует стриплойн и вырезку через кость. Томагавк — рибай с длинной рёберной костью. Для первого заказа: рибай (сочный) или филе миньон (нежный). Для опытного: ти-бон или томагавк.",
        "highlight": "Мраморность — не маркетинг. Это прожилки жира внутри мышцы, которые тают при приготовлении и дают сочность и вкус."
      },
      {
        "heading": "Как оценить хороший стейк",
        "body": "Хороший стейк: корочка золотистая (реакция Майяра), внутри — именно та прожарка, которую вы заказали, мясо отдохнуло (не режьте сразу — дайте 3 минуты после подачи). Плохой стейк: серый снаружи (не была горячая сковорода), неправильная прожарка, вытекает кровь на тарелку сразу (не отдохнул). В «Парусах на крыше» мясо поступает от проверенных поставщиков, а шефы умеют работать с температурой.",
        "list": [
          "Золотистая корочка — реакция Майяра",
          "Дайте стейку отдохнуть 3 минуты после подачи",
          "Сок должен оставаться в мясе, не вытекать",
          "Правильная прожарка — по заказу гостя"
        ]
      }
    ],
    "cta": { "text": "Посмотреть мясное меню", "href": "/menu" }
  },
  {
    "slug": "kak-organizovat-firmennoe-meropriyatie-restoran",
    "title": "Как организовать корпоративное мероприятие в ресторане: чеклист",
    "metaTitle": "Корпоративное мероприятие в ресторане — чеклист | Паруса на крыше",
    "metaDescription": "Полный чеклист для организации корпоратива в ресторане: что согласовать, сколько времени нужно, какие вопросы задать и как не упустить важное.",
    "keywords": "корпоратив ресторан организация, корпоративное мероприятие петербург, как организовать корпоратив ресторан",
    "date": "2026-06-13",
    "category": "Кухня и еда",
    "readTime": 7,
    "image": "/photos/interior.webp",
    "lead": "Организовать корпоратив в ресторане — задача, которая кажется простой, но легко превращается в головную боль без правильной подготовки. Вот чеклист, который поможет не забыть ничего важного.",
    "sections": [
      {
        "heading": "За 3–4 недели до мероприятия",
        "body": "Определите: дату, количество гостей, бюджет (на человека или общий), формат (фуршет, банкет, ужин). Свяжитесь с несколькими ресторанами, запросите коммерческое предложение. Уточните: вместимость зала, наличие технического оборудования (проектор, микрофон), возможность для декора, парковку, время аренды. В «Парусах» вместимость до 300 гостей, есть несколько залов разного размера. Банкетное меню от 2 500 ₽/чел.",
        "list": [
          "Определите формат: фуршет или банкет",
          "Запросите КП у 2–3 ресторанов",
          "Уточните техническое оборудование",
          "Проверьте наличие парковки рядом"
        ]
      },
      {
        "heading": "За 1–2 недели до мероприятия",
        "body": "Согласуйте меню: обязательно учтите вегетарианцев, аллергиков, непьющих. Подтвердите точное число гостей и внесите предоплату. Согласуйте сценарий вечера с координатором ресторана: кто выступает, когда подаются блюда, есть ли паузы для речей. Если нужна живая музыка или ди-джей — договоритесь заранее. В «Парусах» живая музыка — стандартная часть вечера, не дополнительная услуга.",
        "highlight": "Всегда назначьте ответственного человека от вашей стороны. Ресторан координирует сервис, но кто-то должен управлять гостями и программой."
      },
      {
        "heading": "В день мероприятия",
        "body": "Приедьте за 30–40 минут до гостей. Проверьте: расстановку столов, работу техники, наличие карточек с именами (если нужно), декор. Познакомьтесь с координатором ресторана — это ваш главный контакт на вечер. Держите телефон с собой: гости будут уточнять адрес, опаздывать. В «Парусах» адрес: ул. Марата, 5, Olympic Plaza, 6-й этаж, СПб. Можно прислать гостям точную ссылку на карту заранее.",
        "list": [
          "Приехать за 30–40 минут до гостей",
          "Проверить технику и расстановку",
          "Познакомиться с координатором ресторана",
          "Выслать гостям адрес и схему прохода заранее"
        ]
      }
    ],
    "cta": { "text": "Обсудить корпоратив", "href": "/korporativ" }
  },
  {
    "slug": "obednyy-restoran-peterbug-biznes",
    "title": "Деловой обед в Петербурге: как выбрать ресторан для рабочей встречи",
    "metaTitle": "Деловой обед в Петербурге — выбор ресторана | Паруса на крыше",
    "metaDescription": "Как выбрать ресторан для делового обеда в Петербурге: уровень шума, уединённость, сервис, меню. Советы для успешных переговоров за столом.",
    "keywords": "деловой обед петербург ресторан, рабочая встреча ресторан спб, бизнес-ланч для переговоров петербург",
    "date": "2026-06-14",
    "category": "Кухня и еда",
    "readTime": 6,
    "image": "/photos/interior.webp",
    "lead": "Деловой обед — это особый формат. Не просто еда, а пространство для переговоров, которое должно работать на вас, а не против. Правильный ресторан даёт комфорт, создаёт нужную атмосферу и не отвлекает от разговора.",
    "sections": [
      {
        "heading": "На что смотреть при выборе",
        "body": "Уровень шума — главное. Если за столом надо кричать — переговоров не получится. Уединённость: лучше отдельный кабинет или хотя бы стол у стены с расстоянием от соседей. Скорость сервиса: деловой обед обычно ограничен по времени. Уточните, можно ли предзаказать. Парковка: важно, если партнёры приедут на машине. Статус заведения: ресторан сигнализирует об уважении к партнёру — место должно соответствовать уровню встречи.",
        "list": [
          "Уровень шума: тихо = можно говорить",
          "Уединённость: отдельный зал или стол у стены",
          "Скорость сервиса: уточните при бронировании",
          "Парковка: важна для деловых встреч"
        ]
      },
      {
        "heading": "Что заказывать на деловом обеде",
        "body": "Правила просты: ничего слишком сложного в еде (ничего с костями, ничего, что брызгает). Деловой обед — не место для экспериментов. Классика: стейк или рыба + гарнир, суп, салат. Алкоголь: можно предложить бокал вина, но не настаивать. Если партнёр не пьёт — вы тоже не пьёте. Бизнес-ланч в «Парусах» от 590 ₽ включает полноценный обед. Для переговоров выше уровнем — лучше выбрать обед по меню.",
        "highlight": "Кто организовал встречу — тот и оплачивает. Это правило деловой культуры в России. Заранее скажите официанту при бронировании."
      },
      {
        "heading": "Почему «Паруса» подходят для деловой встречи",
        "body": "«Паруса на крыше» — место с историей и репутацией. Работаем с 2009 года. Рейтинг 5,0, 2 606 отзывов. Наличие отдельных залов и тихих зон позволяет провести встречу комфортно. Вид на Петербург с 6-го этажа Olympic Plaza — это небольшой, но важный плюс: он создаёт нужное настроение без лишних слов. Партнёр оценит выбор места. Бизнес-ланч и обед по меню — оба формата доступны. Парковка в Olympic Plaza есть.",
        "list": [
          "Работаем с 2009 года, репутация подтверждена",
          "Отдельные залы — конфиденциальность",
          "Вид с 6-го этажа — важный штрих для впечатления",
          "Паркинг в Olympic Plaza"
        ]
      }
    ],
    "cta": { "text": "Забронировать деловой стол", "href": "/" }
  },
  {
    "slug": "sup-v-restorane-kak-ocenivat",
    "title": "Суп в ресторане: как оценить качество и что стоит заказывать",
    "metaTitle": "Суп в ресторане — как оценить качество | Паруса на крыше",
    "metaDescription": "Как оценить суп в ресторане, что говорит о качестве и мастерстве кухни. Виды супов и советы по выбору первого блюда в меню.",
    "keywords": "суп ресторан качество, как выбрать суп ресторан, первое блюдо ресторан петербург",
    "date": "2026-06-14",
    "category": "Кухня и еда",
    "readTime": 5,
    "image": "/photos/food.webp",
    "lead": "Суп — первое блюдо, которое легко проигнорировать в ресторане. А зря. Хороший суп говорит о кухне больше, чем стейк. Бульон нельзя купить — его варят. Это время и мастерство, которые нельзя сфальсифицировать.",
    "sections": [
      {
        "heading": "Почему суп — тест для кухни",
        "body": "Хороший бульон варится 4–6 часов из костей, овощей и специй. Это нельзя ускорить — только время даёт нужную глубину вкуса. Ресторан, который варит бульон сам, работает честно. Тот, кто использует кубик или порошок, — нет. Это чувствуется сразу: настоящий бульон имеет прозрачность, насыщенный аромат и послевкусие. Кубик — мутный, с химическим привкусом.",
        "list": [
          "Прозрачный бульон — хороший знак",
          "Насыщенный аромат без химии",
          "Долгое послевкусие — признак настоящего бульона",
          "Мутный с резким запахом — кубик"
        ]
      },
      {
        "heading": "Виды супов в меню",
        "body": "Бульоны (консоме) — прозрачные, концентрированные. Пюре-супы (велюте, биск) — пробитые, однородные. Кремовые — с добавлением сливок, насыщенные. Национальные: борщ, уха, солянка, гаспачо. В «Парусах» первые блюда меняются сезонно: зимой — более плотные, летом — лёгкие или холодные. Уха готовится из свежей рыбы — не замороженной.",
        "highlight": "Хорошая солянка в ресторане — знак, что кухня умеет работать с мясом. В ней должны быть минимум 3 вида копчёностей и настоящий бульон."
      },
      {
        "heading": "Как заказывать",
        "body": "Спросите официанта: «Из чего варится бульон?» Хороший ресторан ответит конкретно. Если суп входит в бизнес-ланч — это обычно самый честный показатель качества ежедневной кухни. В «Парусах» бизнес-ланч от 590 ₽ включает первое блюдо. Это простой способ оценить кухню без большого вложения. Если суп хорош — остальное тоже будет на уровне.",
        "list": [
          "Спросите об источнике бульона",
          "Бизнес-ланч: хороший способ оценить кухню",
          "Сезонный суп в меню — свежее обычного",
          "Суп-пюре: должен быть гладким, без комков"
        ]
      }
    ],
    "cta": { "text": "Попробовать бизнес-ланч", "href": "/" }
  },
  {
    "slug": "morozhenoe-dessert-restoran",
    "title": "Мороженое в ресторане: почему хорошее мороженое — это серьёзно",
    "metaTitle": "Мороженое в ресторане — зачем серьёзные шефы берутся за него | Паруса",
    "metaDescription": "Почему рестораны высокого уровня уделяют внимание мороженому. Виды: джелато, сорбет, пломбир. Как отличить домашнее от промышленного.",
    "keywords": "мороженое ресторан, джелато сорбет ресторан, авторское мороженое петербург",
    "date": "2026-06-14",
    "category": "Кухня и еда",
    "readTime": 5,
    "image": "/photos/food.webp",
    "lead": "Мороженое в ресторане — это не afterthought. В лучших заведениях его делают самостоятельно: с правильным составом, температурой подачи и сочетанием вкусов. Это показатель качества кухни и уважения к финалу трапезы.",
    "sections": [
      {
        "heading": "Виды ресторанного мороженого",
        "body": "Джелато — итальянское мороженое с меньшим содержанием жира и воздуха, более интенсивный вкус. Подаётся при более высокой температуре, чем промышленное. Сорбет — без молочных продуктов, из фруктового пюре или сока. Лёгкий, освежающий. Пломбир — классическое советское мороженое на сливках. Мягкое мороженое (soft serve) — стиль без заморозки, нежная консистенция. Авторское мороженое: шеф добавляет нестандартные вкусы — травы, специи, алкоголь.",
        "list": [
          "Джелато: итальянское, интенсивный вкус",
          "Сорбет: без молочного, из фруктов",
          "Пломбир: классика на сливках",
          "Авторское: нестандартные вкусы от шефа"
        ]
      },
      {
        "heading": "Как отличить домашнее от промышленного",
        "body": "Домашнее (ресторанное) мороженое: менее воздушное, более плотное и насыщенное. Вкус чистый — слышен конкретный продукт (ваниль, малина, фисташка). Тает быстрее промышленного — это нормально. Промышленное: пышное за счёт большого количества воздуха (overrun), вкус стандартный, много стабилизаторов. Тает медленнее из-за эмульгаторов.",
        "highlight": "Хорошее мороженое тает быстро — потому что в нём много молока и мало воздуха. Это признак качества, а не брака."
      },
      {
        "heading": "Мороженое как элемент десерта",
        "body": "В хороших ресторанах мороженое — часть сложного десерта: сорбет между горячим и основным десертом (тrou normand), шарик ванильного джелато к горячему брауни, мятный сорбет как финал дегустационного сета. В «Парусах» десерты готовятся ежедневно, включая домашние сорбеты в летнем меню. Финал вечера должен быть не хуже его начала.",
        "list": [
          "Сорбет между курсами — очищает вкус",
          "К горячему брауни — ванильный джелато",
          "Мятный сорбет — классический финал сета",
          "Авторский сорбет с алкоголем — для взрослых"
        ]
      }
    ],
    "cta": { "text": "Посмотреть карту десертов", "href": "/menu" }
  },
  {
    "slug": "banket-menyu-chto-vklyuchaet",
    "title": "Банкетное меню в ресторане: что включает и как его выбирать",
    "metaTitle": "Банкетное меню в ресторане — что включает | Паруса на крыше",
    "metaDescription": "Что такое банкетное меню в ресторане, что в него входит, как выбрать между фуршетом и банкетом, сколько стоит и на что обращать внимание.",
    "keywords": "банкетное меню ресторан, банкет что включает, банкетный ужин петербург ресторан",
    "date": "2026-06-15",
    "category": "Кухня и еда",
    "readTime": 6,
    "image": "/photos/interior.webp",
    "lead": "Банкетное меню — это не просто список блюд. Это заранее согласованный набор, который позволяет ресторану подготовиться и провести мероприятие чётко. Но как выбрать правильный пакет и не переплатить за лишнее?",
    "sections": [
      {
        "heading": "Что включает банкетное меню",
        "body": "Стандартное банкетное меню включает: холодные закуски на стол (нарезки, салаты), горячие закуски, суп (не всегда), горячее основное блюдо с гарниром, десерт, напитки (безалкогольные), хлеб и масло. Алкоголь обычно идёт отдельно или включён в расширенный пакет. В «Парусах на крыше» банкетное меню от 2 500 ₽/чел. Это полноценный ужин с несколькими переменами.",
        "list": [
          "Холодные закуски: нарезки, салаты",
          "Горячее: основное блюдо с гарниром",
          "Десерт: входит в стандарт",
          "Алкоголь: обычно отдельно"
        ]
      },
      {
        "heading": "Банкет vs фуршет",
        "body": "Банкет — гости сидят за столами, блюда подаются поочерёдно. Формально, удобно, торжественно. Фуршет — все стоят, блюда на общих столах. Неформально, помогает общению между гостями, которые не знакомы. Фуршет дешевле на 20–30%: меньше персонала, меньше посуды, проще логистика. Для свадьбы или корпоратива с программой — лучше банкет. Для сетевого ивента или фуршета-знакомства — фуршет. В «Парусах» доступны оба формата.",
        "highlight": "На фуршете гости едят столько, сколько хотят, и когда хотят. На банкете — по расписанию. Если у вас длинная программа — выбирайте банкет."
      },
      {
        "heading": "На что обращать внимание при выборе",
        "body": "Количество блюд: минимум 4–5 перемен для полноценного банкета. Качество продуктов: уточните, используются ли свежие ингредиенты. Персонал: сколько официантов на гостей (норма: 1 на 10–15). Гибкость: можно ли адаптировать под вегетарианцев или аллергиков? Время: сколько часов включает аренда зала. В «Парусах» вместимость до 300 гостей, несколько залов, живая музыка, вид на Петербург — всё включено в атмосферу.",
        "list": [
          "Минимум 4–5 перемен для полного банкета",
          "1 официант на 10–15 гостей",
          "Адаптация под диеты — уточните заранее",
          "Время аренды зала — в договоре"
        ]
      }
    ],
    "cta": { "text": "Обсудить банкет", "href": "/banket" }
  },
  {
    "slug": "ovoshchi-gril-restoran-blyuda",
    "title": "Овощи на гриле в ресторане: почему это серьёзно и что заказывать",
    "metaTitle": "Овощи на гриле в ресторане | Паруса на крыше",
    "metaDescription": "Почему овощи на гриле в ресторане — это серьёзный выбор, а не просто гарнир. Лучшие виды, техники приготовления и как их оценивать.",
    "keywords": "овощи гриль ресторан, гриль-блюда ресторан петербург, овощной гриль меню",
    "date": "2026-06-15",
    "category": "Кухня и еда",
    "readTime": 5,
    "image": "/photos/food.webp",
    "lead": "Овощи на гриле — не просто гарнир. В хорошем ресторане это самостоятельное блюдо с характером. Цветная капуста, обжаренная до карамелизации. Кабачок с лимонным маслом. Спаржа с пармезаном. Разбираем, почему гриль меняет всё.",
    "sections": [
      {
        "heading": "Что происходит с овощами на гриле",
        "body": "Высокая температура гриля запускает реакцию Майяра — ту же, что делает стейк аппетитным. Сахара в овощах карамелизируются, образуя сладковатую корочку. Внутри овощ остаётся нежным, снаружи — хрустящим. Это принципиально другой продукт по сравнению с варёным или запечённым. Именно поэтому «цветная капуста на гриле» в ресторане — это откровение для тех, кто знает цветную капусту только варёной.",
        "list": [
          "Карамелизация сахаров — сладковатая корочка",
          "Реакция Майяра — как при жарке стейка",
          "Внутри нежно, снаружи хрустяще",
          "Дымный аромат — бонус от огня"
        ]
      },
      {
        "heading": "Лучшие овощи для гриля",
        "body": "Цветная капуста: целые соцветия с маслом — трансформируется полностью. Баклажан: мягкий, дымный, лучший для соусов. Кабачок: быстро, нежно, отлично с лимоном. Спаржа: 3 минуты на сильном огне — лучший вариант зелёной спаржи. Перец: жареный перец снимается кожица, остаётся сладкий. Кукуруза: на гриле с маслом и специями — letний хит. Шиитаке: грибной умами усиливается на огне.",
        "highlight": "Баклажан после гриля теряет горечь полностью. Если вам не нравился баклажан до — попробуйте на гриле."
      },
      {
        "heading": "Как заказывать в ресторане",
        "body": "Овощи на гриле часто идут как гарнир, но в хорошем ресторане их можно заказать отдельно. Уточните: с каким соусом или маслом подают? Лимонное масло — классика. Тахини — ближневосточный акцент. Козий сыр или пармезан — для насыщенности. В «Парусах на крыше» меню включает сезонные овощные позиции, которые меняются по сезону. Летом — баклажан, кабачок, спаржа. Осенью — тыква, корень сельдерея, батат.",
        "list": [
          "Уточните: с каким соусом подают",
          "Лимонное масло — универсальная классика",
          "Можно заказать как гарнир или самостоятельное",
          "Сезонные овощи — всегда лучший выбор"
        ]
      }
    ],
    "cta": { "text": "Посмотреть меню", "href": "/menu" }
  },
  {
    "slug": "buterbrod-bruschetta-krostini-raznitsa",
    "title": "Брускетта, крутоны и кростини: разница и зачем они в ресторанном меню",
    "metaTitle": "Брускетта, крутоны, кростини — в чём разница | Паруса на крыше",
    "metaDescription": "Чем отличается брускетта от кростини, что такое крутоны и как все эти итальянские термины связаны с обычным тостом. Гид для тех, кто хочет понять меню.",
    "keywords": "брускетта кростини разница, что такое кростини брускетта, итальянские закуски ресторан",
    "date": "2026-06-15",
    "category": "Кухня и еда",
    "readTime": 4,
    "image": "/photos/food.webp",
    "lead": "Меню ресторана предлагает «кростини с рикоттой» и «брускетту с томатами». Чем они отличаются, кроме начинки? И что такое крутоны — маленькие сухарики или что-то ещё? Объясняем разницу.",
    "sections": [
      {
        "heading": "Брускетта",
        "body": "Брускетта (от brustolare — «подрумянивать») — хлеб, поджаренный на гриле или в духовке, натёртый чесноком и политый оливковым маслом. Это традиционная итальянская закуска, которую крестьяне готовили, чтобы использовать зачерствевший хлеб. Классическая начинка: свежие томаты с базиликом. Но начинки бывают любыми: рикотта с мёдом, авокадо, карпаччо из тунца. Размер: обычно большой, как полный ломоть хлеба.",
        "list": [
          "Хлеб поджарен на гриле или в духовке",
          "Натёрт чесноком + оливковое масло",
          "Размер: большой, один ломоть",
          "Начинка: любая, классика — томаты и базилик"
        ]
      },
      {
        "heading": "Кростини и крутоны",
        "body": "Кростини (crostini — «маленькие тосты») — небольшие ломтики хлеба (обычно багета), поджаренные до хруста. Подаются с начинками: паштет, трюфельная паста, брускетта с рикоттой. Это закуска-аперитив. Крутоны (не «крутоны» из пакета) — маленькие кубики хлеба, поджаренные в оливковом масле. Идут в салаты для текстуры (например, в цезарь). В ресторане крутоны всегда свежие, не из пакета.",
        "highlight": "Брускетта — большая, кростини — маленькие. Крутоны — кубики для салата. Вот и вся разница."
      },
      {
        "heading": "Как это связано с хорошей кухней",
        "body": "В ресторане хорошее отношение к хлебу — признак качества. Если хлеб поджаривают свежим и с правильным маслом — это уважение к деталям. В «Парусах» закуски включают позиции с авторскими начинками на основе этих классических форм. Это не просто тост — это приветствие от кухни, первое, что попадает на стол. Оно задаёт тон всему ужину.",
        "list": [
          "Брускетта: большая, гриль + чеснок + масло",
          "Кростини: маленькие тосты с начинками",
          "Крутоны: кубики для салата",
          "Свежий хлеб с маслом — знак уважения"
        ]
      }
    ],
    "cta": { "text": "Посмотреть меню закусок", "href": "/menu" }
  },
  {
    "slug": "shokolad-dessert-chto-byvat-v-restorane",
    "title": "Шоколадные десерты в ресторане: виды, температура подачи и лучшие сочетания",
    "metaTitle": "Шоколадные десерты в ресторане — виды и сочетания | Паруса",
    "metaDescription": "Виды шоколадных десертов в ресторане: фондан, мусс, брауни, ганаш. Как их оценивать, при какой температуре должны подаваться и с чем сочетать.",
    "keywords": "шоколадный десерт ресторан, фондан шоколадный ресторан, шоколадный мусс ресторан",
    "date": "2026-06-16",
    "category": "Кухня и еда",
    "readTime": 5,
    "image": "/photos/food.webp",
    "lead": "Шоколадные десерты — самый popular финал ресторанного ужина. Но за простым словом «шоколадный» скрываются десятки разных текстур и вкусов. Фондан — не то же самое, что брауни. Мусс — другой, чем ганаш. Разбираемся.",
    "sections": [
      {
        "heading": "Виды шоколадных десертов",
        "body": "Фондан (coulant) — горячий шоколадный кекс с жидкой начинкой внутри. Это главный технический десерт: важно вынуть из формы вовремя, чтобы центр остался жидким. Мусс — лёгкий, воздушный, взбитый. Ганаш — плотный крем из шоколада и сливок. Брауни — плотный, влажный, американский. Трюфели — ганаш, обваленный в какао или шоколаде. Шоколадный торт — слоёный, с разными кремами.",
        "list": [
          "Фондан: горячий, с жидким центром",
          "Мусс: лёгкий, воздушный",
          "Ганаш: плотный крем, насыщенный",
          "Брауни: влажный, плотный, американская классика"
        ]
      },
      {
        "heading": "Как оценить качество",
        "body": "Фондан проверяется просто: разрежьте — должен течь шоколад. Если нет — передержали. Мусс должен быть лёгким и тает на языке. Ганаш — гладким без расслоения. Брауни — влажным, не сухим. Важно: хороший шоколадный десерт зависит от качества шоколада. Горькость, аромат, длинное послевкусие — признаки хорошего шоколада 70%+. В «Парусах» шеф-кондитер работает с проверенными поставщиками шоколада.",
        "highlight": "Настоящий фондан: когда разрезаешь — шоколад течёт. Если внутри просто мягкий шоколадный кекс — его передержали."
      },
      {
        "heading": "С чем сочетать",
        "body": "Шоколад + ваниль: классика. Горький шоколад + малина: кислота режет жирность. Шоколад + карамель + морская соль: тройной контраст. Напитки: портвейн или амаретто к горькому шоколаду. Эспрессо — традиционно. Красное сладкое вино (Banyuls) — французская классика. В «Парусах» к десертам предлагается карта дижестивов и десертных вин — спросите официанта.",
        "list": [
          "Ваниль: классика к любому шоколаду",
          "Малина: кислота балансирует горечь",
          "Портвейн или амаретто: к горькому",
          "Эспрессо: универсальный финал"
        ]
      }
    ],
    "cta": { "text": "Посмотреть десертное меню", "href": "/menu" }
  },
  {
    "slug": "bluda-na-den-valentina-restoran",
    "title": "Романтический ужин 14 февраля: что заказать в ресторане",
    "metaTitle": "День Валентина ужин в ресторане — что заказать | Паруса на крыше",
    "metaDescription": "Что заказать на романтический ужин 14 февраля в ресторане: лучшие блюда для двоих, как не ошибиться с выбором и создать нужную атмосферу.",
    "keywords": "день валентина ресторан, романтический ужин 14 февраля, что заказать день влюблённых ресторан",
    "date": "2026-06-16",
    "category": "Кухня и еда",
    "readTime": 5,
    "image": "/photos/view_sunset.webp",
    "lead": "14 февраля — один из самых загруженных дней в ресторанах. Бронируйте заранее и выбирайте блюда вдумчиво. Романтический ужин — не время для экспериментов, но и не повод заказывать скучно.",
    "sections": [
      {
        "heading": "Блюда, которые работают на атмосферу",
        "body": "Устрицы — классика романтического ужина. Говорят, что они повышают настроение, но важнее другое: их медленно едят, они требуют внимания и создают атмосферу. Тартар из лосося — элегантно и нежно. Ризотто на двоих — уютно и согревает. Стейк — для более уверенного тона. Морепродукты — лёгкие и хорошо выглядят. Главный принцип: еда должна быть фоном для разговора, а не главной темой.",
        "list": [
          "Устрицы: классика романтического старта",
          "Тартар из лосося: нежно и элегантно",
          "Ризотто: уютный выбор",
          "Морепродукты: лёгкие, красивые в подаче"
        ]
      },
      {
        "heading": "Что не стоит заказывать",
        "body": "Избегайте блюд с луком и чесноком в большом количестве. Длинная паста брызгает. Блюда с рёбрами или костями — неудобно. Очень острое — непредсказуемо. Слишком экзотическое, если не уверены в реакции спутника. 14 февраля — не лучший день для первого знакомства с устрицами или тартаром, если человек не пробовал их раньше. Заказывайте то, что точно понравится обоим.",
        "highlight": "Предложите спутнику выбрать самостоятельно — это уважение. Или заранее узнайте предпочтения и сделайте выбор неожиданным."
      },
      {
        "heading": "Десерт и финал вечера",
        "body": "Десерт на двоих — отличный финал: один на двоих пишет о близости. Шоколадный фондан или клубничный тарт — хорошие варианты. Бокал шампанского к десерту — классика. В «Парусах на крыше» 14 февраля работает специальная программа: живая музыка, праздничный декор, меню для двоих. Забронируйте заранее — в этот день свободных мест практически нет. Скидка именинникам: если день рождения совпадает с 14 февраля — 20% на весь счёт именинника.",
        "list": [
          "Десерт на двоих: один на двоих — романтично",
          "Шампанское к десерту: классика",
          "В «Парусах» — живая музыка и спецпрограмма",
          "Бронируйте заранее: мест почти нет"
        ]
      }
    ],
    "cta": { "text": "Забронировать романтический ужин", "href": "/romanticheskiy-uzhin" }
  }
];
